import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import PlayDisabled from "../../images/PlayDisabled.svg";
import Pauseicon from "../../images/Pauseicon.svg";
import SixDotBlueBg from "../../images/sixDotBlueBg.svg";
import Powericon from "../../images/PowerIcon.svg";
import AddKeyframeIcon from "../../images/Add keyframe.svg";
import ImageCompIcon from "../../images/imageComp.svg";
import HitAreaCompIcon from "../../images/hitAreaComp.svg";
import HitAreaCompBlueIcon from "../../images/hitAreaCompBlue.svg";
import Lock from "../../images/Locked1.svg";
import UnLock from "../../images/Unlocked1.svg";
import hideIcon from "../../images/Hide1.svg";
import EyeIcon from "../../images/Show1.svg";
import HitAreaCanvasSection from "./HitAreaModal/HitAreaCanvasSection";
import HitAreaKeyframePopup from "./HitAreaModal/HitAreaKeyframePopup";
import HitAreaTimelineSection from "./HitAreaModal/HitAreaTimelineSection";
import {
  createHitAreaModalPlaybackTimeline,
  getHitAreaModalPreviewElementId,
  killHitAreaModalTimeline,
  resetHitAreaModalPreviewElements,
  scrubHitAreaModalTimeline,
} from "./HitAreaModal/hitAreaModalGsapPlayback";

import * as HitAreaModalUtils from "./HitAreaModal/utils";

const {
  extractImageSource,
  clamp,
  MIN_RECT_SIZE,
  MIN_TIMELINE_DURATION,
  HIT_AREA_TIMELINE_LEFT_INSET,
  HIT_AREA_TIMELINE_MARKER_HEAD_OVERHANG_PX,
  HIT_AREA_MODAL_TIMELINE_DURATION,
  HIT_AREA_TIMELINE_DEFAULT_VISIBLE_SECONDS,
  MAX_HIT_AREA_NAME_LENGTH,
  MAX_HIT_AREAS_PER_IMAGE,
  SNAP_TOLERANCE_PX,
  BOUNDARY_WARNING_THRESHOLD_PX,
  MAX_SMART_FEEDBACK_CANDIDATES,
  KEYFRAME_TIME_EPSILON,
  KEYFRAME_DRAG_START_THRESHOLD_PX,
  KEYFRAME_POPUP_WIDTH,
  KEYFRAME_POPUP_MIN_EDGE_GAP,
  KEYFRAME_POPUP_ARROW_SAFE_GAP,
  KEYFRAME_POPUP_HORIZONTAL_ANCHOR_RATIO,
  KEYFRAME_POPUP_ESTIMATED_HEIGHT,
  KEYFRAME_POPUP_ARROW_SIZE,
  KEYFRAME_POPUP_MARKER_GAP,
  HIT_AREA_LINK_PANEL_DESIRED_WIDTH,
  HIT_AREA_LINK_PANEL_MIN_WIDTH,
  HIT_AREA_LINK_PANEL_MIN_EDGE_GAP,
  HIT_AREA_LIMIT_TOOLTIP,
  HIT_AREA_PREVIEW_SIZE_SCALE,
  computeAspectFitDimensions,
  createDeferredResizeObserver,
  toNumber,
  roundToInt,
  roundDimension,
  formatDimensionForEditor,
  normalizeStoredDimension,
  persistCanvasDimension,
  roundTimelineTime,
  getTimelinePixelsPerSecond,
  getVisibleTimelineSecondsFromDraggerWidth,
  formatVisibleTimelineSeconds,
  sortKeyframesByTime,
  cloneHitAreaKeyframes,
  sanitizeHitAreaKeyframes,
  sanitizeHitArea,
  getKeyframeAtTime,
  getExactKeyframeAtTime,
  getInterpolatedHitAreaRectAtTime,
  getLiveHitAreaCanvasRect,
  applyKeyframeEditorPreviewToRect,
  clampKeyframeEditorDimensionInput,
  patchKeyframeByIdOnly,
  appendHitAreaKeyframe,
  isTimeWithinRange,
  clipKeyframesToRange,
  hasNearbyKeyframeSpacing,
  constrainKeyframeDragTime,
  createDimensionText,
  createDisplayRect,
  normalizeRect,
  getBoundaryDistances,
  resolveBoundaryBadgeCenter,
  isRectNearBoundary,
  isRectNearOtherRects,
  rectanglesOverlap,
  calculateRectDistance,
  interpolateRect,
  constrainRectToNonOverlapByInterpolation,
  constrainDisplayRectMoveToNonOverlap,
  constrainDisplayRectResizeToNonOverlap,
  constrainRectSizeByInterpolation,
  getNextHitAreaLabelIndex,
  getHitAreaDefaultName,
  resolveHitAreaDisplayName,
  validateHitAreaRename,
  sanitizeHitAreaRenameInput,
  normalizeHitAreaUrl,
  isDefaultHitAreaUrlValue,
  getHitAreaLinkInputSeed,
  estimateHitAreaLinkPanelHeight,
  resolveHitAreaLinkPanelPosition,
  getConfiguredClickableAreaCount,
  getClickableAreasConfiguredTooltip,
} = HitAreaModalUtils;

const HIT_AREA_LOCKED_TOAST_ID = "hit-area-component-locked";
const HIT_AREA_DUPLICATE_NAME_TOAST_ID = "hit-area-duplicate-name";
const HIT_AREA_DUPLICATE_NAME_TOAST_MS = 2000;

const showHitAreaComponentLockedToast = () => {
  if (toast.isActive(HIT_AREA_LOCKED_TOAST_ID)) {
    return;
  }
  toast.error(<div className="notificationText">Component is Locked!</div>, {
    toastId: HIT_AREA_LOCKED_TOAST_ID,
  });
};

export default function HitAreaModal({
  open,
  onCancel,
  onSave,
  selectedComponent,
  showTimeline = true,
  timelineSeconds: _timelineSeconds = 0,
  totalDurationInSeconds = HIT_AREA_MODAL_TIMELINE_DURATION,
  totalDurationWidth = 1359,
}) {
  const componentStyles = selectedComponent?.componentStyles || {};
  const imageSource = extractImageSource(componentStyles.background);
  const rawImageWidth = toNumber(componentStyles.width, 0);
  const rawImageHeight = toNumber(componentStyles.height, 0);
  const authoringImageWidth = toNumber(
    componentStyles.hitAreaAuthoringWidth,
    0
  );
  const authoringImageHeight = toNumber(
    componentStyles.hitAreaAuthoringHeight,
    0
  );
  // Prefer live size; when geometry is frozen (0 / below 5×5 / shrink that
  // would crush hit areas), fall back to the last authored size for layout.
  const useAuthoringImageSize =
    !!componentStyles.hitAreaGeometryFrozen ||
    rawImageWidth < MIN_RECT_SIZE ||
    rawImageHeight < MIN_RECT_SIZE;
  const imageWidth = Math.max(
    1,
    useAuthoringImageSize && authoringImageWidth > 0
      ? authoringImageWidth
      : rawImageWidth > 0
        ? rawImageWidth
        : authoringImageWidth > 0
          ? authoringImageWidth
          : 1
  );
  const imageHeight = Math.max(
    1,
    useAuthoringImageSize && authoringImageHeight > 0
      ? authoringImageHeight
      : rawImageHeight > 0
        ? rawImageHeight
        : authoringImageHeight > 0
          ? authoringImageHeight
          : 1
  );
  const layerName =
    componentStyles.layerName || componentStyles.layerNameUpdated || "Image";
  const imageLayerLocked = !!componentStyles.lockBoolean;
  const imageLayerHidden = !!componentStyles.hideBoolean;

  // Core modal state: hit areas, selection, drawing, timeline, and editors.
  const [hitAreas, setHitAreas] = useState([]);
  const [selectedHitAreaId, setSelectedHitAreaId] = useState(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [previewStageDimensions, setPreviewStageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftRect, setDraftRect] = useState(null);
  const [activeTimelineEdit, setActiveTimelineEdit] = useState(null);
  const [activeKeyframeDrag, setActiveKeyframeDrag] = useState(null);
  const [editingHitAreaId, setEditingHitAreaId] = useState(null);
  const [editingHitAreaName, setEditingHitAreaName] = useState("");
  const [isPreviewImageLoaded, setIsPreviewImageLoaded] = useState(false);
  const [isPreviewImageError, setIsPreviewImageError] = useState(false);
  const [smartFeedback, setSmartFeedback] = useState(null);
  const [currentTimelineSecond, setCurrentTimelineSecond] = useState(0);
  const [isTimelinePlaying, setIsTimelinePlaying] = useState(false);
  const [isTimelineMarkerScrubbing, setIsTimelineMarkerScrubbing] = useState(false);
  const [selectedKeyframeId, setSelectedKeyframeId] = useState(null);
  const [keyframeEditorWidth, setKeyframeEditorWidth] = useState("");
  const [keyframeEditorHeight, setKeyframeEditorHeight] = useState("");
  const [pendingKeyframeTime, setPendingKeyframeTime] = useState(null);
  const [keyframeEditorErrors, setKeyframeEditorErrors] = useState({
    width: "",
    height: "",
  });
  const [hitAreaLinkInput, setHitAreaLinkInput] = useState("");
  const [hitAreaLinkError, setHitAreaLinkError] = useState("");
  const [isHitAreaLinkEditing, setIsHitAreaLinkEditing] = useState(false);
  const [hitAreaLinkPanelMeasuredHeight, setHitAreaLinkPanelMeasuredHeight] = useState(0);
  const [linkPanelContainerMetrics, setLinkPanelContainerMetrics] = useState(null);
  const [isDrawToolActive, setIsDrawToolActive] = useState(true);
  const [hoveredHitAreaId, setHoveredHitAreaId] = useState(null);
  const [keyframeClipboard, setKeyframeClipboard] = useState(null);
  const [keyframePopupState, setKeyframePopupState] = useState({
    isOpen: false,
    left: 0,
    top: 0,
    arrowLeft: 0,
    placement: "above",
  });

  // Derived timeline/image metadata used for scaling and active ranges.
  const safeDuration = clamp(
    Math.round(Number(totalDurationInSeconds) || HIT_AREA_MODAL_TIMELINE_DURATION),
    1,
    90
  );
  const safeTimelineWidth = Math.max(
    200,
    Number(totalDurationWidth) || safeDuration * 45
  );
  const secondWidth = safeTimelineWidth / safeDuration;
  const imageRowActivePercent = useMemo(() => {
    const rowWidth = Number(componentStyles.layerWidth);
    if (!Number.isFinite(rowWidth) || rowWidth <= 0 || safeTimelineWidth <= 0) {
      return 82;
    }
    return clamp((rowWidth / safeTimelineWidth) * 100, 6, 100);
  }, [componentStyles.layerWidth, safeTimelineWidth]);
  const imageTimelineDuration = useMemo(
    () =>
      clamp(
        (imageRowActivePercent / 100) * safeDuration,
        MIN_TIMELINE_DURATION,
        safeDuration
      ),
    [imageRowActivePercent, safeDuration]
  );
  const imageTimelineStart = useMemo(
    () =>
      clamp(
        toNumber(componentStyles.timelineStart, 0),
        0,
        safeDuration - MIN_TIMELINE_DURATION
      ),
    [componentStyles.timelineStart, safeDuration]
  );
  const imageTimelineEnd = useMemo(() => {
    const fallbackEnd = imageTimelineStart + imageTimelineDuration;
    return clamp(
      toNumber(componentStyles.timelineEnd, fallbackEnd),
      imageTimelineStart + MIN_TIMELINE_DURATION,
      safeDuration
    );
  }, [
    componentStyles.timelineEnd,
    imageTimelineDuration,
    imageTimelineStart,
    safeDuration,
  ]);
  const imageActiveTimelineDuration = useMemo(
    () =>
      clamp(
        imageTimelineEnd - imageTimelineStart,
        MIN_TIMELINE_DURATION,
        safeDuration
      ),
    [imageTimelineEnd, imageTimelineStart, safeDuration]
  );
  const maxDraggerWidth =
    safeDuration < 60 ? safeDuration * 45 : safeDuration * 44.97;
  const timelinePixelsPerSecond = getTimelinePixelsPerSecond(safeDuration);
  const initialDraggerWidth = clamp(
    HIT_AREA_TIMELINE_DEFAULT_VISIBLE_SECONDS * timelinePixelsPerSecond,
    45,
    maxDraggerWidth
  );
  const restoredDraggerWidth = useMemo(() => {
    const persistedDraggerWidth = toNumber(
      componentStyles?.hitAreaDraggerWidth,
      NaN
    );
    const baseDraggerWidth =
      Number.isFinite(persistedDraggerWidth) && persistedDraggerWidth > 0
        ? persistedDraggerWidth
        : initialDraggerWidth;
    return clamp(baseDraggerWidth, 45, maxDraggerWidth);
  }, [componentStyles?.hitAreaDraggerWidth, initialDraggerWidth, maxDraggerWidth]);
  const [draggerWidth, setDraggerWidth] = useState(restoredDraggerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const visibleTimelineSeconds = useMemo(
    () =>
      clamp(
        getVisibleTimelineSecondsFromDraggerWidth(draggerWidth, safeDuration),
        MIN_TIMELINE_DURATION,
        safeDuration
      ),
    [draggerWidth, safeDuration]
  );
  const timelineText = `${currentTimelineSecond.toFixed(1)} / ${formatVisibleTimelineSeconds(visibleTimelineSeconds)}s`;

  // Mutable refs for drag gestures, playback, and undo/redo history stacks.
  const previewAreaRef = useRef(null);
  const previewStageRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const previewImageRef = useRef(null);
  const drawingStartRef = useRef({ x: 0, y: 0 });
  const draftRectRef = useRef(null);
  const nextDefaultHitAreaIndexRef = useRef(1);
  const uniqueHitAreaSeedRef = useRef(0);
  const hitAreasRef = useRef([]);
  const selectedHitAreaIdRef = useRef(null);
  const historyPastRef = useRef([]);
  const historyFutureRef = useRef([]);
  const historyCommitFrameRef = useRef(null);
  const isHitAreaTransformingRef = useRef(false);
  const timelineLeftColumnRef = useRef(null);
  const timelineRightColumnRef = useRef(null);
  const timelineRightInnerRef = useRef(null);
  const timelineMarkerRef = useRef(null);
  const modalMainRef = useRef(null);
  const timelinePanelRef = useRef(null);
  const timelineScrollSyncRef = useRef(false);
  const timelineMarkerDragRef = useRef(null);
  const keyframeDragMovedRef = useRef(false);
  const timelineRangeDragMovedRef = useRef(false);
  const suppressNextKeyframeClickRef = useRef(false);
  const suppressAutoLinkEditRef = useRef(false);
  const hoverHideTimeoutRef = useRef(null);
  const timelinePlaybackRafRef = useRef(null);
  const hitAreaPlaybackTimelineRef = useRef(null);
  const hitAreaPlaybackFrameRef = useRef(null);
  const hitAreaScrubTimelineRef = useRef(null);
  const currentTimelineSecondRef = useRef(0);
  const timelinePlaybackStartMsRef = useRef(0);
  const timelinePlaybackStartSecondRef = useRef(0);
  // After scrubbing the marker, next Play should restart from 0 (not resume).
  // Soft pause clears this so Play resumes from the pause point.
  const restartPlaybackFromStartRef = useRef(false);
  const selectedKeyframeIdRef = useRef(null);
  const pendingKeyframeTimeRef = useRef(null);
  const isHitAreaLimitReached = hitAreas.length >= MAX_HIT_AREAS_PER_IMAGE;
  const isPreviewStageReady =
    previewStageDimensions.width > 0 && previewStageDimensions.height > 0;
  const canDrawHitAreas =
    !!imageSource &&
    isPreviewImageLoaded &&
    isPreviewStageReady &&
    !isPreviewImageError &&
    !isHitAreaLimitReached;

  const snapTimelineMarkerToStart = useCallback(() => {
    currentTimelineSecondRef.current = 0;
    const markerElement = timelineMarkerRef.current;
    if (markerElement) {
      markerElement.style.left = `${HIT_AREA_TIMELINE_LEFT_INSET}px`;
      markerElement.setAttribute("title", "0.0s");
    }
    flushSync(() => {
      setCurrentTimelineSecond(0);
    });
  }, []);

  const stopTimelinePlayback = useCallback(() => {
    setIsTimelinePlaying(false);
    setIsTimelineMarkerScrubbing(false);
    if (hitAreaPlaybackFrameRef.current) {
      window.cancelAnimationFrame(hitAreaPlaybackFrameRef.current);
      hitAreaPlaybackFrameRef.current = null;
    }
    killHitAreaModalTimeline(hitAreaPlaybackTimelineRef.current, hitAreasRef.current);
    hitAreaPlaybackTimelineRef.current = null;
    killHitAreaModalTimeline(hitAreaScrubTimelineRef.current, hitAreasRef.current);
    hitAreaScrubTimelineRef.current = null;
    if (timelinePlaybackRafRef.current) {
      window.cancelAnimationFrame(timelinePlaybackRafRef.current);
      timelinePlaybackRafRef.current = null;
    }
  }, []);

    // Soft pause: keep playhead time and preview positions so Play can resume.
  const pauseTimelinePlayback = useCallback(() => {
    setIsTimelinePlaying(false);
    setIsTimelineMarkerScrubbing(false);
    restartPlaybackFromStartRef.current = false;
    if (hitAreaPlaybackFrameRef.current) {
      window.cancelAnimationFrame(hitAreaPlaybackFrameRef.current);
      hitAreaPlaybackFrameRef.current = null;
    }
    if (timelinePlaybackRafRef.current) {
      window.cancelAnimationFrame(timelinePlaybackRafRef.current);
      timelinePlaybackRafRef.current = null;
    }
    const timeline = hitAreaPlaybackTimelineRef.current;
    if (timeline) {
      try {
        if (typeof timeline.pause === "function") timeline.pause();
        if (typeof timeline.time === "function") {
          const pausedAt = roundTimelineTime(timeline.time());
          currentTimelineSecondRef.current = pausedAt;
          setCurrentTimelineSecond(pausedAt);
        }
        if (typeof timeline.kill === "function") timeline.kill();
      } catch (e) {
        // Timeline may already be killed.
      }
      hitAreaPlaybackTimelineRef.current = null;
    }
    // Do not reset preview elements or snap marker to 0 — resume needs both.
  }, []);

  const startTimelinePlayback = useCallback(() => {
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    setPendingKeyframeTime(null);
    setSelectedHitAreaId(null);
    setSelectedKeyframeId(null);
    setEditingHitAreaId(null);
    setEditingHitAreaName("");
    setSmartFeedback(null);
    setIsHitAreaLinkEditing(false);
    setHitAreaLinkError("");
    setHoveredHitAreaId(null);

    // Marker was scrubbed → restart from 0. Soft pause → resume from pause time.
    if (restartPlaybackFromStartRef.current) {
      restartPlaybackFromStartRef.current = false;
      snapTimelineMarkerToStart();
      resetHitAreaModalPreviewElements(hitAreasRef.current);
    }

    timelinePlaybackStartSecondRef.current = currentTimelineSecondRef.current;
    timelinePlaybackStartMsRef.current = window.performance.now();
    setIsTimelinePlaying(true);
  }, [snapTimelineMarkerToStart]);

  const toggleTimelinePlayback = useCallback(() => {
    if (isTimelinePlaying) {
      pauseTimelinePlayback();
      return;
    }
    startTimelinePlayback();
  }, [isTimelinePlaying, pauseTimelinePlayback, startTimelinePlayback]);

  const cancelHoverHide = useCallback(() => {
    if (hoverHideTimeoutRef.current) {
      window.clearTimeout(hoverHideTimeoutRef.current);
      hoverHideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHoverHide = useCallback(() => {
    cancelHoverHide();
    hoverHideTimeoutRef.current = window.setTimeout(() => {
      hoverHideTimeoutRef.current = null;
      if (!isHitAreaLinkEditing) {
        setHoveredHitAreaId(null);
      }
    }, 3500);
  }, [cancelHoverHide, isHitAreaLinkEditing]);

  const applyPreviewStageDimensions = useCallback((nextDimensions) => {
    setPreviewStageDimensions((previousDimensions) =>
      previousDimensions.width === nextDimensions.width &&
      previousDimensions.height === nextDimensions.height
        ? previousDimensions
        : nextDimensions
    );
    setStageSize((previousSize) =>
      previousSize.width === nextDimensions.width &&
      previousSize.height === nextDimensions.height
        ? previousSize
        : nextDimensions
    );
  }, []);

  const syncPreviewStageDimensions = useCallback(() => {
    const previewArea = previewAreaRef.current;
    if (!previewArea) return;

    const areaStyles = window.getComputedStyle(previewArea);
    const paddingX =
      (parseFloat(areaStyles.paddingLeft) || 0) +
      (parseFloat(areaStyles.paddingRight) || 0);
    const paddingY =
      (parseFloat(areaStyles.paddingTop) || 0) +
      (parseFloat(areaStyles.paddingBottom) || 0);
    const availableWidth = Math.max(0, previewArea.clientWidth - paddingX);
    const availableHeight = Math.max(0, previewArea.clientHeight - paddingY);

    if (availableWidth <= 0 || availableHeight <= 0) return;

    const nextDimensions = computeAspectFitDimensions(
      imageWidth,
      imageHeight,
      availableWidth * HIT_AREA_PREVIEW_SIZE_SCALE,
      availableHeight * HIT_AREA_PREVIEW_SIZE_SCALE
    );

    applyPreviewStageDimensions(nextDimensions);
  }, [applyPreviewStageDimensions, imageHeight, imageWidth]);

  const refreshStageSize = useCallback(() => {
    syncPreviewStageDimensions();
  }, [syncPreviewStageDimensions]);

  // History helpers: snapshot building, signature checks, and stack updates.
  const cloneHitAreasForHistory = useCallback(
    (areas = []) =>
      areas.map((item) => ({
        ...item,
        keyframes: cloneHitAreaKeyframes(item?.keyframes || []),
      })),
    []
  );

  const createHistorySnapshot = useCallback(
    (areas = [], selectedId = null, nextLabelIndex = 1) => ({
      hitAreas: cloneHitAreasForHistory(areas),
      selectedHitAreaId: selectedId || null,
      nextDefaultHitAreaIndex: Math.max(
        1,
        Math.floor(toNumber(nextLabelIndex, 1))
      ),
    }),
    [cloneHitAreasForHistory]
  );

  const createHistorySnapshotFromRefs = useCallback(
    () =>
      createHistorySnapshot(
        hitAreasRef.current,
        selectedHitAreaIdRef.current,
        nextDefaultHitAreaIndexRef.current
      ),
    [createHistorySnapshot]
  );

  const getHistorySnapshotSignature = useCallback((snapshot) => {
    const normalizedAreas = Array.isArray(snapshot?.hitAreas)
      ? snapshot.hitAreas.map((item) => ({
          id: item?.id || "",
          name: item?.name || "",
          defaultLabelIndex: toNumber(item?.defaultLabelIndex, 0),
          lockBoolean: !!item?.lockBoolean,
          hideBoolean: !!item?.hideBoolean,
          x: roundToInt(item?.x),
          y: roundToInt(item?.y),
          width: roundDimension(item?.width),
          height: roundDimension(item?.height),
          targetLink: item?.targetLink || "",
          timelineStart: Number(toNumber(item?.timelineStart, 0).toFixed(2)),
          timelineEnd: Number(toNumber(item?.timelineEnd, 0).toFixed(2)),
          keyframes: cloneHitAreaKeyframes(item?.keyframes || []),
        }))
      : [];

    return JSON.stringify({
      hitAreas: normalizedAreas,
      selectedHitAreaId: snapshot?.selectedHitAreaId || null,
      nextDefaultHitAreaIndex: Math.max(
        1,
        Math.floor(toNumber(snapshot?.nextDefaultHitAreaIndex, 1))
      ),
    });
  }, []);

  const clearUndoRedoHistory = useCallback(() => {
    if (historyCommitFrameRef.current) {
      window.cancelAnimationFrame(historyCommitFrameRef.current);
      historyCommitFrameRef.current = null;
    }
    isHitAreaTransformingRef.current = false;
    historyPastRef.current = [];
    historyFutureRef.current = [];
  }, []);

  const queueHistoryCommit = useCallback(() => {
    if (!open) return;
    if (historyCommitFrameRef.current) {
      window.cancelAnimationFrame(historyCommitFrameRef.current);
      historyCommitFrameRef.current = null;
    }

    historyCommitFrameRef.current = window.requestAnimationFrame(() => {
      historyCommitFrameRef.current = null;
      const nextSnapshot = createHistorySnapshotFromRefs();
      const pastSnapshots = historyPastRef.current;
      const latestSnapshot = pastSnapshots[pastSnapshots.length - 1];
      if (
        latestSnapshot &&
        getHistorySnapshotSignature(latestSnapshot) ===
          getHistorySnapshotSignature(nextSnapshot)
      ) {
        return;
      }
      historyPastRef.current = [...pastSnapshots, nextSnapshot];
      historyFutureRef.current = [];
    });
  }, [
    createHistorySnapshotFromRefs,
    getHistorySnapshotSignature,
    open,
  ]);

  const applyHistorySnapshot = useCallback(
    (snapshot) => {
      const areas = cloneHitAreasForHistory(snapshot?.hitAreas || []);
      const requestedSelectedId = snapshot?.selectedHitAreaId || null;
      const selectedId = areas.some((item) => item.id === requestedSelectedId)
        ? requestedSelectedId
        : areas[0]?.id || null;
      const nextLabelIndex = Math.max(
        1,
        Math.floor(
          toNumber(
            snapshot?.nextDefaultHitAreaIndex,
            getNextHitAreaLabelIndex(areas)
          )
        )
      );
      const selectedArea = areas.find((item) => item.id === selectedId) || null;
      const activeFrame = getKeyframeAtTime(
        selectedArea?.keyframes || [],
        currentTimelineSecond
      );

      setHitAreas(areas);
      setSelectedHitAreaId(selectedId);
      setSelectedKeyframeId(activeFrame?.id || selectedArea?.keyframes?.[0]?.id || null);
      setActiveTimelineEdit(null);
      setActiveKeyframeDrag(null);
      setEditingHitAreaId(null);
      setEditingHitAreaName("");
      setSmartFeedback(null);
      setKeyframePopupState((previousState) =>
        previousState.isOpen ? { ...previousState, isOpen: false } : previousState
      );
      setIsDrawing(false);
      setDraftRect(null);
      draftRectRef.current = null;
      isHitAreaTransformingRef.current = false;
      keyframeDragMovedRef.current = false;
      suppressNextKeyframeClickRef.current = false;
      nextDefaultHitAreaIndexRef.current = nextLabelIndex;
    },
    [cloneHitAreasForHistory, currentTimelineSecond]
  );

  const undoHistory = useCallback(() => {
    if (!open) return;
    if (historyCommitFrameRef.current) {
      window.cancelAnimationFrame(historyCommitFrameRef.current);
      historyCommitFrameRef.current = null;
    }

    const pastSnapshots = historyPastRef.current;
    if (pastSnapshots.length <= 1) return;

    const currentSnapshot = pastSnapshots[pastSnapshots.length - 1];
    const previousSnapshot = pastSnapshots[pastSnapshots.length - 2];
    historyPastRef.current = pastSnapshots.slice(0, -1);
    historyFutureRef.current = [
      ...historyFutureRef.current,
      currentSnapshot,
    ];
    applyHistorySnapshot(previousSnapshot);
  }, [applyHistorySnapshot, open]);

  const redoHistory = useCallback(() => {
    if (!open) return;
    if (historyCommitFrameRef.current) {
      window.cancelAnimationFrame(historyCommitFrameRef.current);
      historyCommitFrameRef.current = null;
    }

    const futureSnapshots = historyFutureRef.current;
    if (!futureSnapshots.length) return;

    const nextSnapshot = futureSnapshots[futureSnapshots.length - 1];
    historyFutureRef.current = futureSnapshots.slice(0, -1);
    historyPastRef.current = [...historyPastRef.current, nextSnapshot];
    applyHistorySnapshot(nextSnapshot);
  }, [applyHistorySnapshot, open]);

  // Initialize modal state from persisted hit areas whenever modal opens.
  useEffect(() => {
    if (!open) return;

    const existingHitAreas = Array.isArray(componentStyles?.hitAreas)
      ? componentStyles.hitAreas
      : [];
    // When geometry is frozen or live size is below 5px, sanitize against
    // the authored size so hit areas are not crushed into a tiny box.
    const sanitizeWidth = useAuthoringImageSize ? imageWidth : rawImageWidth;
    const sanitizeHeight = useAuthoringImageSize ? imageHeight : rawImageHeight;
    const sanitizedHitAreas = existingHitAreas.map((item, index) =>
      sanitizeHitArea(
        item,
        index,
        sanitizeWidth,
        sanitizeHeight,
        safeDuration,
        imageTimelineStart,
        imageTimelineEnd
      )
    );

    const initialSelectedHitAreaId = null;
    const initialSelectedKeyframeId = null;
    setHitAreas(sanitizedHitAreas);
    const persistedNextLabelIndex = Math.max(
      1,
      Math.floor(toNumber(componentStyles?.hitAreaSequence, 1))
    );
    const nextDefaultHitAreaIndex = Math.max(
      getNextHitAreaLabelIndex(sanitizedHitAreas),
      persistedNextLabelIndex
    );
    nextDefaultHitAreaIndexRef.current = nextDefaultHitAreaIndex;
    uniqueHitAreaSeedRef.current = 0;
    setSelectedHitAreaId(initialSelectedHitAreaId);
    setSelectedKeyframeId(initialSelectedKeyframeId);
    currentTimelineSecondRef.current = 0;
    setCurrentTimelineSecond(0);
    setActiveTimelineEdit(null);
    setActiveKeyframeDrag(null);
    setEditingHitAreaId(null);
    setEditingHitAreaName("");
    setSmartFeedback(null);
    setKeyframePopupState({
      isOpen: false,
      left: 0,
      top: 0,
      arrowLeft: 0,
      placement: "above",
    });
    setKeyframeClipboard(null);
    setKeyframeEditorErrors({ width: "", height: "" });
    setHitAreaLinkInput("");
    setHitAreaLinkError("");
    setIsHitAreaLinkEditing(false);
    setIsDrawToolActive(true);
    suppressAutoLinkEditRef.current = false;
    setHoveredHitAreaId(null);
    cancelHoverHide();
    setIsPreviewImageLoaded(false);
    setIsPreviewImageError(false);
    setIsDrawing(false);
    setDraftRect(null);
    stopTimelinePlayback();
    draftRectRef.current = null;
    isHitAreaTransformingRef.current = false;
    keyframeDragMovedRef.current = false;
    suppressNextKeyframeClickRef.current = false;
    timelineRangeDragMovedRef.current = false;
    setDraggerWidth(restoredDraggerWidth);
    setIsResizing(false);
    hitAreasRef.current = cloneHitAreasForHistory(sanitizedHitAreas);
    selectedHitAreaIdRef.current = initialSelectedHitAreaId;
    historyPastRef.current = [
      createHistorySnapshot(
        sanitizedHitAreas,
        initialSelectedHitAreaId,
        nextDefaultHitAreaIndex
      ),
    ];
    historyFutureRef.current = [];
    if (historyCommitFrameRef.current) {
      window.cancelAnimationFrame(historyCommitFrameRef.current);
      historyCommitFrameRef.current = null;
    }
  }, [
    cancelHoverHide,
    cloneHitAreasForHistory,
    componentStyles?.hitAreas,
    componentStyles?.hitAreaSequence,
    componentStyles?.hitAreaDraggerWidth,
    createHistorySnapshot,
    imageHeight,
    imageTimelineEnd,
    imageTimelineStart,
    imageWidth,
    maxDraggerWidth,
    open,
    restoredDraggerWidth,
    safeDuration,
    stopTimelinePlayback,
  ]);

  useEffect(() => {
    hitAreasRef.current = hitAreas;
  }, [hitAreas]);

  useEffect(() => {
    selectedHitAreaIdRef.current = selectedHitAreaId;
  }, [selectedHitAreaId]);

  useEffect(() => {
    selectedKeyframeIdRef.current = selectedKeyframeId;
  }, [selectedKeyframeId]);

  useEffect(() => {
    pendingKeyframeTimeRef.current = pendingKeyframeTime;
  }, [pendingKeyframeTime]);

  useEffect(() => {
    currentTimelineSecondRef.current = currentTimelineSecond;
  }, [currentTimelineSecond]);

  useEffect(() => {
    if (!hoveredHitAreaId) return;
    const stillExists = hitAreas.some((item) => item.id === hoveredHitAreaId);
    if (!stillExists) {
      setHoveredHitAreaId(null);
    }
  }, [hitAreas, hoveredHitAreaId]);

  useEffect(
    () => () => {
      if (hoverHideTimeoutRef.current) {
        window.clearTimeout(hoverHideTimeoutRef.current);
        hoverHideTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (open) return;
    stopTimelinePlayback();
    setActiveKeyframeDrag(null);
    keyframeDragMovedRef.current = false;
    suppressNextKeyframeClickRef.current = false;
    timelineRangeDragMovedRef.current = false;
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    setKeyframeClipboard(null);
    setHitAreaLinkInput("");
    setHitAreaLinkError("");
    setIsHitAreaLinkEditing(false);
    setIsDrawToolActive(true);
    suppressAutoLinkEditRef.current = false;
    setHoveredHitAreaId(null);
    cancelHoverHide();
    clearUndoRedoHistory();
  }, [cancelHoverHide, clearUndoRedoHistory, open, stopTimelinePlayback]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      const hasModifier = event.metaKey || event.ctrlKey;
      if (!hasModifier) return;
      const pressedKey = String(event.key || "").toLowerCase();
      const isUndoShortcut = pressedKey === "z" && !event.shiftKey;
      const isRedoShortcut =
        pressedKey === "r" ||
        pressedKey === "y" ||
        (pressedKey === "z" && event.shiftKey);

      if (!isUndoShortcut && !isRedoShortcut) return;
      event.preventDefault();
      event.stopPropagation();

      if (
        isTimelinePlaying ||
        isDrawing ||
        activeTimelineEdit ||
        activeKeyframeDrag ||
        isHitAreaTransformingRef.current
      ) {
        return;
      }

      if (isUndoShortcut) {
        undoHistory();
        return;
      }

      redoHistory();
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    activeKeyframeDrag,
    activeTimelineEdit,
    isDrawing,
    isTimelinePlaying,
    open,
    redoHistory,
    undoHistory,
  ]);

  useEffect(() => {
    if (!open) return;
    setPreviewStageDimensions({ width: 0, height: 0 });
    setStageSize({ width: 0, height: 0 });
  }, [open, imageSource, imageWidth, imageHeight]);

  useLayoutEffect(() => {
    if (!open) return;
    syncPreviewStageDimensions();
  }, [
    open,
    imageSource,
    imageWidth,
    imageHeight,
    isPreviewImageLoaded,
    syncPreviewStageDimensions,
  ]);

  useEffect(() => {
    if (!open) return;
    refreshStageSize();
    const handleViewportChange = () => {
      refreshStageSize();
    };

    window.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    let resizeObserver;
    const observedElements = [previewAreaRef.current, canvasAreaRef.current].filter(
      Boolean
    );
    if (observedElements.length) {
      resizeObserver = createDeferredResizeObserver(handleViewportChange);
      observedElements.forEach((element) => resizeObserver?.observe(element));
    }

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
      resizeObserver?.cancelPendingFrame?.();
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [open, imageSource, imageWidth, imageHeight, refreshStageSize]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerRelease = () => {
      isHitAreaTransformingRef.current = false;
    };

    window.addEventListener("mouseup", handlePointerRelease, true);
    window.addEventListener("blur", handlePointerRelease);
    return () => {
      window.removeEventListener("mouseup", handlePointerRelease, true);
      window.removeEventListener("blur", handlePointerRelease);
    };
  }, [open]);

  const getStageDimensions = useCallback(() => {
    if (previewStageDimensions.width > 0 && previewStageDimensions.height > 0) {
      return previewStageDimensions;
    }
    return { width: stageSize.width || 0, height: stageSize.height || 0 };
  }, [
    previewStageDimensions.height,
    previewStageDimensions.width,
    stageSize.height,
    stageSize.width,
  ]);

  // Convert between display-space rectangles and intrinsic image-space rectangles.
  const displayToImageRect = useCallback(
    (displayRect) => {
      const stageDimensions = getStageDimensions();
      const widthScale =
        stageDimensions.width > 0 ? imageWidth / stageDimensions.width : 1;
      const heightScale =
        stageDimensions.height > 0 ? imageHeight / stageDimensions.height : 1;

      const width = clamp(
        roundToInt(displayRect.width * widthScale),
        0,
        Math.max(1, imageWidth)
      );
      const height = clamp(
        roundToInt(displayRect.height * heightScale),
        0,
        Math.max(1, imageHeight)
      );
      const x = clamp(
        roundToInt(displayRect.x * widthScale),
        0,
        Math.max(0, imageWidth - width)
      );
      const y = clamp(
        roundToInt(displayRect.y * heightScale),
        0,
        Math.max(0, imageHeight - height)
      );

      return { x, y, width, height };
    },
    [getStageDimensions, imageHeight, imageWidth]
  );

  const imageToDisplayRect = useCallback(
    (hitArea) => {
      const stageDimensions = getStageDimensions();
      const widthScale =
        imageWidth > 0 ? stageDimensions.width / imageWidth : 1;
      const heightScale =
        imageHeight > 0 ? stageDimensions.height / imageHeight : 1;

      return {
        x: toNumber(hitArea?.x) * widthScale,
        y: toNumber(hitArea?.y) * heightScale,
        width: Math.max(MIN_RECT_SIZE, toNumber(hitArea?.width) * widthScale),
        height: Math.max(
          MIN_RECT_SIZE,
          toNumber(hitArea?.height) * heightScale
        ),
      };
    },
    [getStageDimensions, imageHeight, imageWidth]
  );

  // Editor uses live canvas rect; playback/scrubbing uses keyframe interpolation.
  const getResolvedHitAreaRectAtTime = useCallback(
    (hitArea, timeInSeconds) => {
      if (!hitArea) return null;
      if (isTimelinePlaying || isTimelineMarkerScrubbing) {
        return getInterpolatedHitAreaRectAtTime(
          hitArea,
          timeInSeconds,
          imageWidth,
          imageHeight
        );
      }
      return getLiveHitAreaCanvasRect(hitArea);
    },
    [imageHeight, imageWidth, isTimelineMarkerScrubbing, isTimelinePlaying]
  );

  // Build smart visual feedback: snap guides, spacing lines, and boundary badges.
  const buildSmartFeedbackFromDisplayRect = useCallback(
    (hitAreaId, displayRect) => {
      const stageDimensions = getStageDimensions();
      if (stageDimensions.width <= 0 || stageDimensions.height <= 0) {
        return null;
      }

      const widthScale = imageWidth > 0 ? stageDimensions.width / imageWidth : 1;
      const heightScale =
        imageHeight > 0 ? stageDimensions.height / imageHeight : 1;

      const selectedImageRect = displayToImageRect(displayRect);
      const selectedDisplayRect = {
        x: selectedImageRect.x * widthScale,
        y: selectedImageRect.y * heightScale,
        width: selectedImageRect.width * widthScale,
        height: selectedImageRect.height * heightScale,
      };

      const selectedLeft = selectedImageRect.x;
      const selectedRight = selectedImageRect.x + selectedImageRect.width;
      const selectedTop = selectedImageRect.y;
      const selectedBottom = selectedImageRect.y + selectedImageRect.height;
      const selectedCenterX = selectedImageRect.x + selectedImageRect.width / 2;
      const selectedCenterY = selectedImageRect.y + selectedImageRect.height / 2;

      const boundaryDistances = getBoundaryDistances(
        selectedImageRect,
        imageWidth,
        imageHeight
      );

      const boundaryBadges = ["top", "left", "right", "bottom"].map((side) => {
        const distance = boundaryDistances[side];
        const { x, y } = resolveBoundaryBadgeCenter({
          side,
          displayRect: selectedDisplayRect,
          stageWidth: stageDimensions.width,
          stageHeight: stageDimensions.height,
        });

        return {
          key: side,
          label: `${roundToInt(distance)} px`,
          x,
          y,
          isWarning: distance < BOUNDARY_WARNING_THRESHOLD_PX,
        };
      });

      const normalizeDisplayY = (value) => clamp(value, 0, stageDimensions.height);
      const normalizeDisplayX = (value) => clamp(value, 0, stageDimensions.width);
      const snapGuideMap = new Map();
      const gapLines = [];

      const otherRects = hitAreas
        .filter((item) => item.id !== hitAreaId)
        .map((item) =>
          getResolvedHitAreaRectAtTime(item, currentTimelineSecond)
        )
        .filter(Boolean);
      const feedbackRects =
        hitAreas.length >= 5 && otherRects.length > MAX_SMART_FEEDBACK_CANDIDATES
          ? [...otherRects]
              .sort(
                (firstRect, secondRect) =>
                  calculateRectDistance(selectedImageRect, firstRect) -
                  calculateRectDistance(selectedImageRect, secondRect)
              )
              .slice(0, MAX_SMART_FEEDBACK_CANDIDATES)
          : otherRects;

      const pushSnapGuide = (orientation, imagePosition, rangeStart, rangeEnd) => {
        const axisKey = `${orientation}-${roundToInt(imagePosition)}`;
        const existing = snapGuideMap.get(axisKey);
        const start = Math.min(rangeStart, rangeEnd);
        const end = Math.max(rangeStart, rangeEnd);
        if (!existing) {
          snapGuideMap.set(axisKey, {
            id: axisKey,
            orientation,
            imagePosition,
            rangeStart: start,
            rangeEnd: end,
          });
          return;
        }
        existing.rangeStart = Math.min(existing.rangeStart, start);
        existing.rangeEnd = Math.max(existing.rangeEnd, end);
      };

      feedbackRects.forEach((otherRect, index) => {
        const otherLeft = otherRect.x;
        const otherRight = otherRect.x + otherRect.width;
        const otherTop = otherRect.y;
        const otherBottom = otherRect.y + otherRect.height;
        const otherCenterX = otherRect.x + otherRect.width / 2;
        const otherCenterY = otherRect.y + otherRect.height / 2;

        const verticalCandidates = [
          [selectedLeft, otherLeft],
          [selectedLeft, otherRight],
          [selectedRight, otherLeft],
          [selectedRight, otherRight],
          [selectedCenterX, otherCenterX],
        ];
        verticalCandidates.forEach(([sourceValue, targetValue]) => {
          if (Math.abs(sourceValue - targetValue) <= SNAP_TOLERANCE_PX) {
            pushSnapGuide(
              "vertical",
              targetValue,
              Math.min(selectedTop, otherTop),
              Math.max(selectedBottom, otherBottom)
            );
          }
        });

        const horizontalCandidates = [
          [selectedTop, otherTop],
          [selectedTop, otherBottom],
          [selectedBottom, otherTop],
          [selectedBottom, otherBottom],
          [selectedCenterY, otherCenterY],
        ];
        horizontalCandidates.forEach(([sourceValue, targetValue]) => {
          if (Math.abs(sourceValue - targetValue) <= SNAP_TOLERANCE_PX) {
            pushSnapGuide(
              "horizontal",
              targetValue,
              Math.min(selectedLeft, otherLeft),
              Math.max(selectedRight, otherRight)
            );
          }
        });

        const overlapTop = Math.max(selectedTop, otherTop);
        const overlapBottom = Math.min(selectedBottom, otherBottom);
        if (overlapBottom > overlapTop) {
          if (selectedRight <= otherLeft) {
            const gap = otherLeft - selectedRight;
            const centerY = ((overlapTop + overlapBottom) / 2) * heightScale;
            gapLines.push({
              id: `gap-h-right-${index}`,
              orientation: "horizontal",
              x1: normalizeDisplayX(selectedRight * widthScale),
              y1: normalizeDisplayY(centerY),
              x2: normalizeDisplayX(otherLeft * widthScale),
              y2: normalizeDisplayY(centerY),
              label: `${roundToInt(gap)} px`,
              isWarning: gap < BOUNDARY_WARNING_THRESHOLD_PX,
            });
          } else if (otherRight <= selectedLeft) {
            const gap = selectedLeft - otherRight;
            const centerY = ((overlapTop + overlapBottom) / 2) * heightScale;
            gapLines.push({
              id: `gap-h-left-${index}`,
              orientation: "horizontal",
              x1: normalizeDisplayX(otherRight * widthScale),
              y1: normalizeDisplayY(centerY),
              x2: normalizeDisplayX(selectedLeft * widthScale),
              y2: normalizeDisplayY(centerY),
              label: `${roundToInt(gap)} px`,
              isWarning: gap < BOUNDARY_WARNING_THRESHOLD_PX,
            });
          }
        }

        const overlapLeft = Math.max(selectedLeft, otherLeft);
        const overlapRight = Math.min(selectedRight, otherRight);
        if (overlapRight > overlapLeft) {
          if (selectedBottom <= otherTop) {
            const gap = otherTop - selectedBottom;
            const centerX = ((overlapLeft + overlapRight) / 2) * widthScale;
            gapLines.push({
              id: `gap-v-bottom-${index}`,
              orientation: "vertical",
              x1: normalizeDisplayX(centerX),
              y1: normalizeDisplayY(selectedBottom * heightScale),
              x2: normalizeDisplayX(centerX),
              y2: normalizeDisplayY(otherTop * heightScale),
              label: `${roundToInt(gap)} px`,
              isWarning: gap < BOUNDARY_WARNING_THRESHOLD_PX,
            });
          } else if (otherBottom <= selectedTop) {
            const gap = selectedTop - otherBottom;
            const centerX = ((overlapLeft + overlapRight) / 2) * widthScale;
            gapLines.push({
              id: `gap-v-top-${index}`,
              orientation: "vertical",
              x1: normalizeDisplayX(centerX),
              y1: normalizeDisplayY(otherBottom * heightScale),
              x2: normalizeDisplayX(centerX),
              y2: normalizeDisplayY(selectedTop * heightScale),
              label: `${roundToInt(gap)} px`,
              isWarning: gap < BOUNDARY_WARNING_THRESHOLD_PX,
            });
          }
        }
      });

      const snapGuides = Array.from(snapGuideMap.values()).map((guide) => {
        if (guide.orientation === "vertical") {
          return {
            ...guide,
            x: guide.imagePosition * widthScale,
            y1: guide.rangeStart * heightScale,
            y2: guide.rangeEnd * heightScale,
          };
        }
        return {
          ...guide,
          y: guide.imagePosition * heightScale,
          x1: guide.rangeStart * widthScale,
          x2: guide.rangeEnd * widthScale,
        };
      });

      return {
        boundaryBadges,
        snapGuides,
        gapLines,
      };
    },
    [
      currentTimelineSecond,
      displayToImageRect,
      getResolvedHitAreaRectAtTime,
      getStageDimensions,
      hitAreas,
      imageHeight,
      imageWidth,
    ]
  );

  const applyAlignmentSnapToDisplayRect = useCallback(
    (hitAreaId, candidateRect, mode = "move", direction = "") => {
      if (!candidateRect) return candidateRect;
      const stageDimensions = getStageDimensions();
      if (stageDimensions.width <= 0 || stageDimensions.height <= 0) {
        return candidateRect;
      }

      const others = hitAreas
        .filter((item) => item.id !== hitAreaId)
        .map((item) =>
          imageToDisplayRect(
            getResolvedHitAreaRectAtTime(item, currentTimelineSecond) || item
          )
        );
      if (!others.length) {
        return candidateRect;
      }

      const rect = {
        x: candidateRect.x,
        y: candidateRect.y,
        width: candidateRect.width,
        height: candidateRect.height,
      };

      const collectTargetValues = (axis) => {
        const values = [];
        others.forEach((item) => {
          if (axis === "x") {
            values.push(item.x, item.x + item.width, item.x + item.width / 2);
          } else {
            values.push(item.y, item.y + item.height, item.y + item.height / 2);
          }
        });
        return values;
      };

      const findNearestSnapDelta = (sourceValues, targetValues) => {
        let chosenDelta = null;
        sourceValues.forEach((sourceValue) => {
          targetValues.forEach((targetValue) => {
            const delta = targetValue - sourceValue;
            if (Math.abs(delta) > SNAP_TOLERANCE_PX) return;
            if (chosenDelta === null || Math.abs(delta) < Math.abs(chosenDelta)) {
              chosenDelta = delta;
            }
          });
        });
        return chosenDelta;
      };

      const directionLower = String(direction || "").toLowerCase();
      const moveLeft = directionLower.includes("left");
      const moveRight = directionLower.includes("right");
      const moveTop = directionLower.includes("top");
      const moveBottom = directionLower.includes("bottom");

      const left = rect.x;
      const right = rect.x + rect.width;
      const top = rect.y;
      const bottom = rect.y + rect.height;
      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;

      const xTargets = collectTargetValues("x");
      const yTargets = collectTargetValues("y");

      let xSources = [];
      let ySources = [];
      if (mode === "move") {
        xSources = [left, right, centerX];
        ySources = [top, bottom, centerY];
      } else {
        if (moveLeft && !moveRight) xSources = [left];
        if (moveRight && !moveLeft) xSources = [right];
        if (moveTop && !moveBottom) ySources = [top];
        if (moveBottom && !moveTop) ySources = [bottom];
      }

      const snapDeltaX = findNearestSnapDelta(xSources, xTargets);
      const snapDeltaY = findNearestSnapDelta(ySources, yTargets);

      if (mode === "move") {
        rect.x += snapDeltaX || 0;
        rect.y += snapDeltaY || 0;
        rect.x = clamp(rect.x, 0, Math.max(0, stageDimensions.width - rect.width));
        rect.y = clamp(rect.y, 0, Math.max(0, stageDimensions.height - rect.height));
        return rect;
      }

      if (moveLeft && !moveRight && snapDeltaX !== null) {
        const rightEdge = rect.x + rect.width;
        const nextLeft = clamp(
          rect.x + snapDeltaX,
          0,
          rightEdge - MIN_RECT_SIZE
        );
        rect.x = nextLeft;
        rect.width = Math.max(MIN_RECT_SIZE, rightEdge - nextLeft);
      }
      if (moveRight && !moveLeft && snapDeltaX !== null) {
        const nextRight = clamp(
          rect.x + rect.width + snapDeltaX,
          rect.x + MIN_RECT_SIZE,
          stageDimensions.width
        );
        rect.width = Math.max(MIN_RECT_SIZE, nextRight - rect.x);
      }
      if (moveTop && !moveBottom && snapDeltaY !== null) {
        const bottomEdge = rect.y + rect.height;
        const nextTop = clamp(
          rect.y + snapDeltaY,
          0,
          bottomEdge - MIN_RECT_SIZE
        );
        rect.y = nextTop;
        rect.height = Math.max(MIN_RECT_SIZE, bottomEdge - nextTop);
      }
      if (moveBottom && !moveTop && snapDeltaY !== null) {
        const nextBottom = clamp(
          rect.y + rect.height + snapDeltaY,
          rect.y + MIN_RECT_SIZE,
          stageDimensions.height
        );
        rect.height = Math.max(MIN_RECT_SIZE, nextBottom - rect.y);
      }

      rect.x = clamp(rect.x, 0, Math.max(0, stageDimensions.width - rect.width));
      rect.y = clamp(rect.y, 0, Math.max(0, stageDimensions.height - rect.height));
      return rect;
    },
    [
      currentTimelineSecond,
      getResolvedHitAreaRectAtTime,
      getStageDimensions,
      hitAreas,
      imageToDisplayRect,
    ]
  );

  // Selected/hovered context and active keyframe/link panel derivations.
  const selectedHitArea = useMemo(
    () => hitAreas.find((item) => item.id === selectedHitAreaId) || null,
    [hitAreas, selectedHitAreaId]
  );
  const isSelectedHitAreaLocked = useMemo(
    () => !!selectedHitArea?.lockBoolean,
    [selectedHitArea]
  );
  const isSelectedHitAreaVisible = useMemo(
    () => !selectedHitArea?.hideBoolean,
    [selectedHitArea]
  );
  const selectedHitAreaTargetLink = String(selectedHitArea?.targetLink || "");

  const handleHitAreaLinkPanelHeightChange = useCallback((height) => {
    const nextHeight = Math.max(0, Math.round(Number(height) || 0));
    setHitAreaLinkPanelMeasuredHeight((previousHeight) =>
      previousHeight === nextHeight ? previousHeight : nextHeight
    );
  }, []);

  const isHitAreaActiveAtCurrentTime = useCallback(
    (hitArea) => {
      if (!hitArea) return false;
      if (hitArea?.hideBoolean) return false;
      const timelineStart = clamp(
        toNumber(hitArea?.timelineStart, imageTimelineStart),
        imageTimelineStart,
        imageTimelineEnd - MIN_TIMELINE_DURATION
      );
      const timelineEndFallback = timelineStart + imageActiveTimelineDuration;
      const timelineEnd = clamp(
        toNumber(hitArea?.timelineEnd, timelineEndFallback),
        timelineStart + MIN_TIMELINE_DURATION,
        imageTimelineEnd
      );
      return isTimeWithinRange(currentTimelineSecond, timelineStart, timelineEnd);
    },
    [
      currentTimelineSecond,
      imageActiveTimelineDuration,
      imageTimelineEnd,
      imageTimelineStart,
    ]
  );

  const selectedHitAreaKeyframe = useMemo(() => {
    if (!selectedHitArea || pendingKeyframeTime !== null) return null;
    if (!selectedKeyframeId) return null;
    return (
      (selectedHitArea.keyframes || []).find(
        (frame) => String(frame.id) === String(selectedKeyframeId)
      ) || null
    );
  }, [pendingKeyframeTime, selectedHitArea, selectedKeyframeId]);

  const hasKeyframeClipboard = useMemo(() => !!keyframeClipboard, [keyframeClipboard]);

  const selectedDisplayRect = useMemo(() => {
    if (!selectedHitArea) return null;
    const keyframePreviewTime =
      pendingKeyframeTime ??
      selectedHitAreaKeyframe?.time ??
      currentTimelineSecond;
    const overlapObstacles = hitAreas
      .filter(
        (item) => item.id !== selectedHitArea.id && !item.hideBoolean
      )
      .map((item) =>
        getResolvedHitAreaRectAtTime(item, keyframePreviewTime)
      )
      .filter(Boolean);
    const previewRect = applyKeyframeEditorPreviewToRect({
      rect: getLiveHitAreaCanvasRect(selectedHitArea),
      hitArea: selectedHitArea,
      imageWidth,
      imageHeight,
      editorWidth: keyframeEditorWidth,
      editorHeight: keyframeEditorHeight,
      isPreviewActive: keyframePopupState.isOpen,
      overlapObstacles,
    });
    return imageToDisplayRect(previewRect);
  }, [
    currentTimelineSecond,
    hitAreas,
    imageHeight,
    imageWidth,
    imageToDisplayRect,
    keyframeEditorHeight,
    keyframeEditorWidth,
    keyframePopupState.isOpen,
    getResolvedHitAreaRectAtTime,
    pendingKeyframeTime,
    selectedHitArea,
    selectedHitAreaKeyframe,
  ]);

  const getCurrentSelectedHitAreaRect = useCallback(() => {
    return getLiveHitAreaCanvasRect(selectedHitArea);
  }, [selectedHitArea]);

  const hitAreaDisplayRectById = useMemo(() => {
    const nextMap = {};
    hitAreas.forEach((item) => {
      const resolvedRect =
        getResolvedHitAreaRectAtTime(item, currentTimelineSecond) || item;
      nextMap[item.id] = imageToDisplayRect(resolvedRect);
    });
    return nextMap;
  }, [
    currentTimelineSecond,
    getResolvedHitAreaRectAtTime,
    hitAreas,
    imageToDisplayRect,
  ]);

  const activeLinkPanelHitArea = useMemo(() => {
    if (!selectedHitArea || selectedHitArea?.hideBoolean) return null;
    return selectedHitArea;
  }, [selectedHitArea]);

  useEffect(() => {
    setHitAreaLinkPanelMeasuredHeight(0);
  }, [activeLinkPanelHitArea?.id]);

  const updateLinkPanelContainerMetrics = useCallback(() => {
    const canvasArea = canvasAreaRef.current;
    const stage = previewStageRef.current;
    if (!canvasArea || !stage || !activeLinkPanelHitArea) {
      setLinkPanelContainerMetrics(null);
      return;
    }

    const canvasRect = canvasArea.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    if (canvasRect.width <= 0 || canvasRect.height <= 0) {
      setLinkPanelContainerMetrics(null);
      return;
    }

    setLinkPanelContainerMetrics({
      width: canvasRect.width,
      height: canvasRect.height,
      stageOffsetX: stageRect.left - canvasRect.left,
      stageOffsetY: stageRect.top - canvasRect.top,
    });
  }, [activeLinkPanelHitArea]);

  const activeLinkPanelDisplayRect = useMemo(() => {
    if (!activeLinkPanelHitArea) return null;
    if (selectedHitArea && activeLinkPanelHitArea.id === selectedHitArea.id) {
      return selectedDisplayRect;
    }
    return hitAreaDisplayRectById[activeLinkPanelHitArea.id] || null;
  }, [
    activeLinkPanelHitArea,
    hitAreaDisplayRectById,
    selectedDisplayRect,
    selectedHitArea,
  ]);

  useLayoutEffect(() => {
    if (!activeLinkPanelHitArea) {
      setLinkPanelContainerMetrics(null);
      return undefined;
    }

    updateLinkPanelContainerMetrics();

    const canvasArea = canvasAreaRef.current;
    const stage = previewStageRef.current;
    const resizeObserver = createDeferredResizeObserver(
      updateLinkPanelContainerMetrics
    );
    if (!canvasArea || !resizeObserver) {
      return undefined;
    }

    resizeObserver.observe(canvasArea);
    if (stage) {
      resizeObserver.observe(stage);
    }

    return () => {
      resizeObserver.cancelPendingFrame();
      resizeObserver.disconnect();
    };
  }, [
    activeLinkPanelDisplayRect,
    activeLinkPanelHitArea,
    hitAreaLinkPanelMeasuredHeight,
    stageSize.height,
    stageSize.width,
    updateLinkPanelContainerMetrics,
  ]);

  // Floating link panel placement with overlap avoidance around active hit area.
  const hitAreaLinkPanelStyle = useMemo(() => {
    if (!activeLinkPanelDisplayRect || !linkPanelContainerMetrics) return null;
    const containerWidth = linkPanelContainerMetrics.width;
    const containerHeight = linkPanelContainerMetrics.height;
    const hitAreaRectInContainer = {
      x: linkPanelContainerMetrics.stageOffsetX + activeLinkPanelDisplayRect.x,
      y: linkPanelContainerMetrics.stageOffsetY + activeLinkPanelDisplayRect.y,
      width: activeLinkPanelDisplayRect.width,
      height: activeLinkPanelDisplayRect.height,
    };
    const maxPanelWidth = Math.max(
      HIT_AREA_LINK_PANEL_MIN_WIDTH,
      containerWidth - HIT_AREA_LINK_PANEL_MIN_EDGE_GAP * 2
    );
    const panelWidth = clamp(
      HIT_AREA_LINK_PANEL_DESIRED_WIDTH,
      HIT_AREA_LINK_PANEL_MIN_WIDTH,
      maxPanelWidth
    );
    const hasSavedLinkOnPanel =
      activeLinkPanelHitArea &&
      !isDefaultHitAreaUrlValue(activeLinkPanelHitArea.targetLink);
    const isLinkPanelEditing =
      isHitAreaLinkEditing || !hasSavedLinkOnPanel;
    const linkPanelDisplayText = isLinkPanelEditing
      ? hitAreaLinkInput
      : getHitAreaLinkInputSeed(activeLinkPanelHitArea?.targetLink);
    const estimatedPanelHeight = estimateHitAreaLinkPanelHeight({
      linkText: linkPanelDisplayText,
      panelWidth,
      isEditing: isLinkPanelEditing,
      hasError: !!hitAreaLinkError,
    });
    const panelEstimatedHeight =
      hitAreaLinkPanelMeasuredHeight || estimatedPanelHeight;

    return resolveHitAreaLinkPanelPosition({
      hitAreaRect: hitAreaRectInContainer,
      panelWidth,
      panelHeight: panelEstimatedHeight,
      panelGap: 8,
      containerWidth,
      containerHeight,
    });
  }, [
    activeLinkPanelDisplayRect,
    activeLinkPanelHitArea,
    hitAreaLinkError,
    hitAreaLinkInput,
    hitAreaLinkPanelMeasuredHeight,
    isHitAreaLinkEditing,
    linkPanelContainerMetrics,
  ]);

  const isSelectedHitAreaActive = useMemo(
    () =>
      isSelectedHitAreaVisible && isHitAreaActiveAtCurrentTime(selectedHitArea),
    [isHitAreaActiveAtCurrentTime, isSelectedHitAreaVisible, selectedHitArea]
  );

  useEffect(() => {
    if (!isSelectedHitAreaActive) {
      setSmartFeedback(null);
    }
  }, [isSelectedHitAreaActive]);

  const selectedImageRectAtCurrentTime = useMemo(() => {
    if (!selectedHitArea) return null;
    const baseRect = getLiveHitAreaCanvasRect(selectedHitArea);
    return applyKeyframeEditorPreviewToRect({
      rect: baseRect,
      hitArea: selectedHitArea,
      imageWidth,
      imageHeight,
      editorWidth: keyframeEditorWidth,
      editorHeight: keyframeEditorHeight,
      isPreviewActive: keyframePopupState.isOpen,
    });
  }, [
    imageHeight,
    imageWidth,
    keyframeEditorHeight,
    keyframeEditorWidth,
    keyframePopupState.isOpen,
    selectedHitArea,
  ]);

  const syncKeyframeEditorFromHitAreaRect = useCallback(
    (hitArea) => {
      const rect = getLiveHitAreaCanvasRect(hitArea);
      if (!rect) return;
      setKeyframeEditorWidth(formatDimensionForEditor(rect.width));
      setKeyframeEditorHeight(formatDimensionForEditor(rect.height));
      setKeyframeEditorErrors({ width: "", height: "" });
    },
    []
  );

  const syncHitAreaLiveFromKeyframe = useCallback((hitArea, keyframe) => {
    if (!hitArea || !keyframe) return hitArea;
    return {
      ...hitArea,
      x: roundToInt(keyframe.x ?? hitArea.x),
      y: roundToInt(keyframe.y ?? hitArea.y),
      width: roundDimension(keyframe.width ?? hitArea.width),
      height: roundDimension(keyframe.height ?? hitArea.height),
    };
  }, []);

  const boundaryWarningIds = useMemo(() => {
    const warnings = new Set();
    const resolvedEntries = hitAreas.map((item) => ({
      id: item.id,
      rect: getResolvedHitAreaRectAtTime(item, currentTimelineSecond) || item,
    }));

    resolvedEntries.forEach((entry) => {
      if (isRectNearBoundary(entry.rect, imageWidth, imageHeight)) {
        warnings.add(entry.id);
        return;
      }

      const otherRects = resolvedEntries
        .filter((other) => other.id !== entry.id)
        .map((other) => other.rect);

      if (isRectNearOtherRects(entry.rect, otherRects)) {
        warnings.add(entry.id);
      }
    });

    return warnings;
  }, [
    currentTimelineSecond,
    getResolvedHitAreaRectAtTime,
    hitAreas,
    imageHeight,
    imageWidth,
  ]);

  useEffect(() => {
    if (pendingKeyframeTime !== null) return;
    if (!selectedHitArea) {
      if (selectedKeyframeId !== null) {
        setSelectedKeyframeId(null);
      }
      return;
    }
    const allFrames = sortKeyframesByTime(selectedHitArea.keyframes || []);
    if (!allFrames.length) {
      if (selectedKeyframeId !== null) {
        setSelectedKeyframeId(null);
      }
      return;
    }
    const hasSelectedFrame = allFrames.some(
      (frame) => String(frame.id) === String(selectedKeyframeId)
    );
    if (hasSelectedFrame) return;
    const exactFrame = getExactKeyframeAtTime(
      allFrames,
      currentTimelineSecond
    );
    setSelectedKeyframeId(exactFrame?.id || null);
  }, [currentTimelineSecond, pendingKeyframeTime, selectedHitArea, selectedKeyframeId]);

  useEffect(() => {
    if (!selectedHitAreaId) {
      setHitAreaLinkInput("");
      setHitAreaLinkError("");
      setIsHitAreaLinkEditing(false);
      suppressAutoLinkEditRef.current = false;
      return;
    }
    setHitAreaLinkInput(getHitAreaLinkInputSeed(selectedHitAreaTargetLink));
    setHitAreaLinkError("");
    if (suppressAutoLinkEditRef.current) {
      suppressAutoLinkEditRef.current = false;
      return;
    }
    setIsHitAreaLinkEditing(isDefaultHitAreaUrlValue(selectedHitAreaTargetLink));
  }, [selectedHitAreaId, selectedHitAreaTargetLink]);

  useEffect(() => {
    if (!selectedHitArea?.lockBoolean) return;
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    setPendingKeyframeTime(null);
  }, [selectedHitArea?.id, selectedHitArea?.lockBoolean]);

  useEffect(() => {
    if (!isHitAreaLinkEditing) return;
    if (!selectedHitArea?.lockBoolean) return;
    setIsHitAreaLinkEditing(false);
  }, [isHitAreaLinkEditing, selectedHitArea]);

  useEffect(() => {
    if (!selectedHitArea) {
      setKeyframeEditorWidth("");
      setKeyframeEditorHeight("");
      setKeyframeEditorErrors({ width: "", height: "" });
      return;
    }

    if (pendingKeyframeTime !== null) {
      if (keyframePopupState.isOpen) {
        syncKeyframeEditorFromHitAreaRect(selectedHitArea);
      }
      return;
    }

    if (!selectedHitAreaKeyframe) {
      setKeyframeEditorWidth("");
      setKeyframeEditorHeight("");
      setKeyframeEditorErrors({ width: "", height: "" });
      return;
    }
    syncKeyframeEditorFromHitAreaRect(selectedHitArea);
  }, [
    keyframePopupState.isOpen,
    pendingKeyframeTime,
    selectedHitArea,
    selectedHitAreaKeyframe,
    syncKeyframeEditorFromHitAreaRect,
  ]);

  const closeKeyframePopup = useCallback(() => {
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    setKeyframeEditorErrors({ width: "", height: "" });
    // Discard any pending (uncommitted) new keyframe.
    setPendingKeyframeTime(null);
    if (selectedHitAreaKeyframe) {
      setKeyframeEditorWidth(formatDimensionForEditor(selectedHitAreaKeyframe.width));
      setKeyframeEditorHeight(formatDimensionForEditor(selectedHitAreaKeyframe.height));
    }
  }, [selectedHitAreaKeyframe]);

  const dismissKeyframePopupForSelectionChange = useCallback(() => {
    const isPopupActive =
      keyframePopupState.isOpen || pendingKeyframeTime !== null;
    if (!isPopupActive) {
      return true;
    }

    if (keyframePopupState.isOpen && selectedHitAreaKeyframe) {
      const parsedWidth = Number(keyframeEditorWidth);
      const parsedHeight = Number(keyframeEditorHeight);
      const isEditorInvalid =
        !Number.isFinite(parsedWidth) ||
        !Number.isFinite(parsedHeight) ||
        parsedWidth <= 0 ||
        parsedHeight <= 0 ||
        parsedWidth < MIN_RECT_SIZE ||
        parsedHeight < MIN_RECT_SIZE;

      // Invalid values (e.g. W/H < 5) — close without confirm so switching layers is free.
      if (!isEditorInvalid) {
        const hasUnsaved =
          roundDimension(parsedWidth) !==
            roundDimension(selectedHitAreaKeyframe.width) ||
          roundDimension(parsedHeight) !==
            roundDimension(selectedHitAreaKeyframe.height);
        if (
          hasUnsaved &&
          !window.confirm(
            "You have unsaved keyframe changes. Discard and open another keyframe?"
          )
        ) {
          return false;
        }
      }
    }

    closeKeyframePopup();
    return true;
  }, [
    closeKeyframePopup,
    keyframeEditorHeight,
    keyframeEditorWidth,
    keyframePopupState.isOpen,
    pendingKeyframeTime,
    selectedHitAreaKeyframe,
  ]);

  const selectHitAreaFromCanvas = useCallback(
    (hitAreaId) => {
      if (isTimelinePlaying) return false;
      const isSwitchingHitArea =
        String(selectedHitAreaId || "") !== String(hitAreaId || "");
      if (isSwitchingHitArea && !dismissKeyframePopupForSelectionChange()) {
        return false;
      }

      setSelectedHitAreaId(hitAreaId);
      if (isSwitchingHitArea) {
        snapTimelineMarkerToStart();
      }
      const hitArea = hitAreas.find((item) => item.id === hitAreaId) || null;
      const exactFrame = getExactKeyframeAtTime(
        hitArea?.keyframes || [],
        isSwitchingHitArea ? 0 : currentTimelineSecond
      );
      setSelectedKeyframeId(exactFrame?.id || null);
      return true;
    },
    [
      currentTimelineSecond,
      dismissKeyframePopupForSelectionChange,
      hitAreas,
      isTimelinePlaying,
      selectedHitAreaId,
      snapTimelineMarkerToStart,
    ]
  );

  const openKeyframePopupFromMarkerElement = useCallback((markerElement) => {
    const modalMainElement = modalMainRef.current;
    if (!modalMainElement || !markerElement) return;

    const containerBounds = modalMainElement.getBoundingClientRect();
    const markerBounds = markerElement.getBoundingClientRect();
    const markerTop = markerBounds.top - containerBounds.top;
    const markerCenterX =
      markerBounds.left + markerBounds.width / 2 - containerBounds.left;

    const minLeft = KEYFRAME_POPUP_MIN_EDGE_GAP;
    const maxLeft = Math.max(
      minLeft,
      containerBounds.width - KEYFRAME_POPUP_WIDTH - KEYFRAME_POPUP_MIN_EDGE_GAP
    );
    const popupLeft = clamp(
      markerCenterX - KEYFRAME_POPUP_WIDTH * KEYFRAME_POPUP_HORIZONTAL_ANCHOR_RATIO,
      minLeft,
      maxLeft
    );
    const arrowLeft = clamp(
      markerCenterX - popupLeft,
      KEYFRAME_POPUP_ARROW_SAFE_GAP,
      KEYFRAME_POPUP_WIDTH - KEYFRAME_POPUP_ARROW_SAFE_GAP
    );

    const arrowClearance = KEYFRAME_POPUP_ARROW_SIZE / 2 + KEYFRAME_POPUP_MARKER_GAP;
    const popupTop =
      markerTop - KEYFRAME_POPUP_ESTIMATED_HEIGHT - arrowClearance;

    setKeyframePopupState({
      isOpen: true,
      left: popupLeft,
      top: popupTop,
      arrowLeft,
      placement: "above",
    });
  }, []);

  useEffect(() => {
    if (!selectedHitAreaId) {
      setSmartFeedback(null);
      setKeyframePopupState((previousState) =>
        previousState.isOpen ? { ...previousState, isOpen: false } : previousState
      );
    }
  }, [selectedHitAreaId]);

  // Row-level mutators and link editor actions from canvas/timeline controls.
  const updateHitAreaById = useCallback((id, updates) => {
    setHitAreas((previousAreas) =>
      previousAreas.map((item) =>
        item.id === id
          ? {
              ...item,
              ...updates,
            }
          : item
      )
    );
  }, []);

  const toggleHitAreaLockById = useCallback(
    (id, event) => {
      if (isTimelinePlaying) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const targetArea = hitAreasRef.current.find((item) => item.id === id);
      const willLock = targetArea && !targetArea.lockBoolean;
      if (willLock) {
        setKeyframePopupState((previousState) =>
          previousState.isOpen ? { ...previousState, isOpen: false } : previousState
        );
        setPendingKeyframeTime(null);
      }
      setHitAreas((previousAreas) =>
        previousAreas.map((item) =>
          item.id === id
            ? {
                ...item,
                lockBoolean: !item?.lockBoolean,
              }
            : item
        )
      );
      queueHistoryCommit();
    },
    [isTimelinePlaying, queueHistoryCommit]
  );

  const toggleHitAreaVisibilityById = useCallback(
    (id, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      let nextHiddenValue = false;
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== id) return item;
          nextHiddenValue = !item?.hideBoolean;
          return {
            ...item,
            hideBoolean: nextHiddenValue,
          };
        })
      );

      if (nextHiddenValue) {
        setHoveredHitAreaId((currentId) => (currentId === id ? null : currentId));
        setSmartFeedback(null);
        setKeyframePopupState((previousState) =>
          previousState.isOpen ? { ...previousState, isOpen: false } : previousState
        );
      }
      queueHistoryCommit();
    },
    [queueHistoryCommit]
  );

  const beginLinkEditForHitArea = useCallback(
    (hitArea) => {
      if (!hitArea) return;
      if (hitArea?.lockBoolean) return;
      if (!dismissKeyframePopupForSelectionChange()) return;
      suppressAutoLinkEditRef.current = true;
      setSelectedHitAreaId(hitArea.id);
      const activeFrame = getKeyframeAtTime(
        hitArea.keyframes || [],
        currentTimelineSecond
      );
      setSelectedKeyframeId(activeFrame?.id || hitArea?.keyframes?.[0]?.id || null);
      setHitAreaLinkError("");
      setHitAreaLinkInput(getHitAreaLinkInputSeed(hitArea.targetLink));
      setIsHitAreaLinkEditing(true);
    },
    [
      currentTimelineSecond,
      dismissKeyframePopupForSelectionChange,
    ]
  );

  const commitSelectedHitAreaLink = useCallback(
    (rawValue, options = {}) => {
      if (!selectedHitArea) return false;
      if (selectedHitArea?.lockBoolean) return false;
      const { closeEditorOnSuccess = false } = options;
      const normalizedValue = normalizeHitAreaUrl(rawValue);
      if (normalizedValue !== selectedHitAreaTargetLink) {
        updateHitAreaById(selectedHitArea.id, { targetLink: normalizedValue });
        queueHistoryCommit();
      }
      setHitAreaLinkInput(
        normalizedValue ? normalizedValue : getHitAreaLinkInputSeed(normalizedValue)
      );
      setHitAreaLinkError("");
      if (closeEditorOnSuccess) {
        setIsHitAreaLinkEditing(false);
      }
      return true;
    },
    [
      queueHistoryCommit,
      selectedHitArea,
      selectedHitAreaTargetLink,
      updateHitAreaById,
    ]
  );

  const getOtherHitAreaImageRectsAtTime = useCallback(
    (ignoreId, timeInSeconds = currentTimelineSecond, areas) => {
      const sourceAreas = areas || hitAreas;
      return sourceAreas
        .filter((item) => item.id !== ignoreId && !item.hideBoolean)
        .map(
          (item) =>
            getResolvedHitAreaRectAtTime(item, timeInSeconds) || normalizeRect(item)
        )
        .filter(Boolean);
    },
    [currentTimelineSecond, getResolvedHitAreaRectAtTime, hitAreas]
  );

  // Geometry guards to keep hit areas valid while drawing/moving/resizing.
  const overlapsOtherHitAreas = useCallback(
    (candidateRect, areas, ignoreId, timeInSeconds = currentTimelineSecond) => {
      if (!candidateRect || candidateRect.width <= 0 || candidateRect.height <= 0) {
        return false;
      }

      return getOtherHitAreaImageRectsAtTime(
        ignoreId,
        timeInSeconds,
        areas || hitAreas
      ).some((compareRect) => rectanglesOverlap(candidateRect, compareRect));
    },
    [currentTimelineSecond, getOtherHitAreaImageRectsAtTime, hitAreas]
  );

  const constrainDisplayRectToNonOverlap = useCallback(
    (
      targetDisplayRect,
      areas,
      ignoreId,
      fallbackDisplayRect,
      timeInSeconds = currentTimelineSecond,
      options = {}
    ) => {
      if (!targetDisplayRect) {
        return fallbackDisplayRect || null;
      }

      const { resizeDirection = "", transformMode = "" } = options;
      const obstacles = (areas || hitAreas)
        .filter((item) => item.id !== ignoreId && !item.hideBoolean)
        .map(
          (item) =>
            getResolvedHitAreaRectAtTime(item, timeInSeconds) || normalizeRect(item)
        )
        .filter(Boolean);
      const targetImageRect = displayToImageRect(targetDisplayRect);
      const fallbackImageRect = fallbackDisplayRect
        ? displayToImageRect(fallbackDisplayRect)
        : null;
      let resolvedImageRect = null;

      if (resizeDirection) {
        const stageDimensions = getStageDimensions();
        const startDisplayRect =
          options.anchorDisplayRect || fallbackDisplayRect || targetDisplayRect;
        const obstacleDisplayRects = obstacles.map((item) => imageToDisplayRect(item));
        const resolvedDisplayRect = constrainDisplayRectResizeToNonOverlap(
          targetDisplayRect,
          startDisplayRect,
          obstacleDisplayRects,
          stageDimensions.width,
          stageDimensions.height,
          resizeDirection
        );
        return resolvedDisplayRect || startDisplayRect || null;
      } else if (transformMode === "move") {
        const stageDimensions = getStageDimensions();
        const obstacleDisplayRects = obstacles.map((item) => imageToDisplayRect(item));
        const resolvedDisplayRect = constrainDisplayRectMoveToNonOverlap(
          targetDisplayRect,
          fallbackDisplayRect || targetDisplayRect,
          obstacleDisplayRects,
          stageDimensions.width,
          stageDimensions.height,
          { anchorRect: options.anchorDisplayRect || fallbackDisplayRect || targetDisplayRect }
        );
        return resolvedDisplayRect || fallbackDisplayRect || null;
      } else {
        resolvedImageRect = constrainRectToNonOverlapByInterpolation(
          targetImageRect,
          fallbackImageRect,
          obstacles
        );
      }

      if (!resolvedImageRect) {
        return fallbackDisplayRect || null;
      }

      return imageToDisplayRect(resolvedImageRect);
    },
    [
      currentTimelineSecond,
      displayToImageRect,
      getResolvedHitAreaRectAtTime,
      getStageDimensions,
      hitAreas,
      imageHeight,
      imageWidth,
      imageToDisplayRect,
    ]
  );

  const updateHitAreaFromDisplayRect = useCallback(
    (id, displayRect, options = {}) => {
      const mode = options.mode === "resize" ? "resize" : "move";
      const timeInSeconds = toNumber(
        options.timeInSeconds,
        currentTimelineSecond
      );
      setHitAreas((previousAreas) => {
        const existingArea = previousAreas.find((item) => item.id === id);
        if (!existingArea) {
          return previousAreas;
        }
        if (existingArea?.lockBoolean) {
          return previousAreas;
        }

        const existingLiveRect = getLiveHitAreaCanvasRect(existingArea);
        const existingDisplayRect = imageToDisplayRect(existingLiveRect);
        const skipReconstraint =
          isHitAreaTransformingRef.current &&
          (mode === "resize" || mode === "move");
        const resolvedDisplayRect = skipReconstraint
          ? displayRect
          : constrainDisplayRectToNonOverlap(
              displayRect,
              previousAreas,
              id,
              existingDisplayRect,
              timeInSeconds
            );
        if (!resolvedDisplayRect) {
          return previousAreas;
        }

        const nextImageRect = displayToImageRect(resolvedDisplayRect);
        const isCanvasResize = mode === "resize";
        const existingFrameRect = existingLiveRect;
        const hasPositionChanged =
          roundToInt(existingFrameRect.x) !== roundToInt(nextImageRect.x) ||
          roundToInt(existingFrameRect.y) !== roundToInt(nextImageRect.y);
        const isLiveCanvasTransform =
          isHitAreaTransformingRef.current && (mode === "resize" || mode === "move");
        const nextWidth = isCanvasResize
          ? isLiveCanvasTransform
            ? persistCanvasDimension(nextImageRect.width, existingFrameRect.width)
            : normalizeStoredDimension(nextImageRect.width, {
                fromCanvasResize: true,
              })
          : normalizeStoredDimension(existingFrameRect.width, {
              fromCanvasResize: false,
            });
        const nextHeight = isCanvasResize
          ? isLiveCanvasTransform
            ? persistCanvasDimension(nextImageRect.height, existingFrameRect.height)
            : normalizeStoredDimension(nextImageRect.height, {
                fromCanvasResize: true,
              })
          : normalizeStoredDimension(existingFrameRect.height, {
              fromCanvasResize: false,
            });
        const hasDimensionChanged =
          normalizeStoredDimension(existingFrameRect.width, {
            fromCanvasResize: isCanvasResize,
          }) !== nextWidth ||
          normalizeStoredDimension(existingFrameRect.height, {
            fromCanvasResize: isCanvasResize,
          }) !== nextHeight;
        const hasChanged =
          hasPositionChanged || (isCanvasResize && hasDimensionChanged);
        if (!hasChanged) {
          return previousAreas;
        }

        const keyframeRect = {
          x: roundToInt(nextImageRect.x),
          y: roundToInt(nextImageRect.y),
          width: nextWidth,
          height: nextHeight,
        };

        return previousAreas.map((item) => {
          if (item.id !== id) return item;

          return {
            ...item,
            ...keyframeRect,
          };
        });
      });
    },
    [
      constrainDisplayRectToNonOverlap,
      currentTimelineSecond,
      displayToImageRect,
      imageToDisplayRect,
    ]
  );

  const deleteHitAreaById = useCallback((id, event) => {
    if (!id || id === "image-row") return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    }
    setHoveredHitAreaId((currentId) => (currentId === id ? null : currentId));
    setKeyframeClipboard((currentClipboard) =>
      currentClipboard?.hitAreaId === id ? null : currentClipboard
    );

    setHitAreas((previousAreas) => {
      const targetArea = previousAreas.find((item) => item.id === id);
      if (!targetArea || targetArea?.lockBoolean) {
        return previousAreas;
      }
      const remainingAreas = previousAreas.filter((item) => item.id !== id);
      setSelectedHitAreaId((currentId) =>
        currentId === id ? remainingAreas[0]?.id || null : currentId
      );
      setSelectedKeyframeId((currentKeyframeId) => {
        const deletedArea = previousAreas.find((item) => item.id === id);
        const deletedKeyframeIds = new Set(
          (deletedArea?.keyframes || []).map((frame) => String(frame.id))
        );
        if (!deletedKeyframeIds.has(String(currentKeyframeId))) {
          return currentKeyframeId;
        }
        return remainingAreas[0]?.keyframes?.[0]?.id || null;
      });
      setEditingHitAreaId((currentId) => {
        if (currentId === id) {
          setEditingHitAreaName("");
          setSmartFeedback(null);
          return null;
        }
        return currentId;
      });
      return remainingAreas;
    });
    queueHistoryCommit();
  }, [queueHistoryCommit]);

  // Canvas draw lifecycle: start draft on mouse down, commit new hit area on mouse up.
  const getPointWithinStage = useCallback(
    (clientX, clientY) => {
      const stageEl = previewStageRef.current;
      if (!stageEl) return { x: 0, y: 0 };

      const bounds = stageEl.getBoundingClientRect();
      const stageDimensions = getStageDimensions();
      if (
        stageDimensions.width <= 0 ||
        stageDimensions.height <= 0 ||
        bounds.width <= 0 ||
        bounds.height <= 0
      ) {
        return { x: 0, y: 0 };
      }

      return {
        x: clamp(
          ((clientX - bounds.left) / bounds.width) * stageDimensions.width,
          0,
          stageDimensions.width
        ),
        y: clamp(
          ((clientY - bounds.top) / bounds.height) * stageDimensions.height,
          0,
          stageDimensions.height
        ),
      };
    },
    [getStageDimensions]
  );

  const handleStageMouseDown = (event) => {
    if (isTimelinePlaying) {
      event.preventDefault();
      return;
    }
    // mousedown fires before blur on the rename input. Commit it here so drawing
    // (or any canvas click) always closes the rename field.
    if (editingHitAreaId) {
      finishHitAreaRename(true);
    }

    if (!isDrawToolActive || !canDrawHitAreas || event.button !== 0) return;
    if (isHitAreaLimitReached) {
      event.preventDefault();
      return;
    }

    if (event.target.closest("[data-hit-area-interactive='true']")) {
      return;
    }

    // mousedown fires BEFORE blur on the focused URL input. If the user was
    // editing a link, we must commit it here – by the time onBlur fires,
    // setSelectedHitAreaId(null) below will have already set isHitAreaLinkEditing
    // to false via the selectedHitAreaId effect, causing onBlur to exit early.
    if (isHitAreaLinkEditing) {
      commitSelectedHitAreaLink(hitAreaLinkInput, {});
      setIsHitAreaLinkEditing(false);
    }

    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    cancelHoverHide();
    setHoveredHitAreaId(null);
    setSelectedHitAreaId(null);
    setSelectedKeyframeId(null);
    setSmartFeedback(null);
    const startPoint = getPointWithinStage(event.clientX, event.clientY);
    drawingStartRef.current = startPoint;
    const initialRect = {
      x: startPoint.x,
      y: startPoint.y,
      width: 0,
      height: 0,
    };
    setDraftRect(initialRect);
    draftRectRef.current = initialRect;
    setIsDrawing(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDrawing) return;

    const handleMouseMove = (event) => {
      const currentPoint = getPointWithinStage(event.clientX, event.clientY);
      const nextDraftRect = createDisplayRect(
        drawingStartRef.current,
        currentPoint
      );
      const fallbackDraftRect = draftRectRef.current || {
        x: drawingStartRef.current.x,
        y: drawingStartRef.current.y,
        width: 0,
        height: 0,
      };
      const resolvedDraftRect = constrainDisplayRectToNonOverlap(
        nextDraftRect,
        hitAreas,
        null,
        fallbackDraftRect,
        currentTimelineSecond
      );
      if (!resolvedDraftRect) {
        return;
      }

      setDraftRect(resolvedDraftRect);
      draftRectRef.current = resolvedDraftRect;
    };

    const handleMouseUp = () => {
      setIsDrawing(false);
      const finalRect = draftRectRef.current;
      let hasCreatedHitArea = false;

      if (finalRect) {
        const nextImageRect = displayToImageRect(finalRect);
        if (
          nextImageRect.width >= MIN_RECT_SIZE &&
          nextImageRect.height >= MIN_RECT_SIZE &&
          !isHitAreaLimitReached
        ) {
          const nextLabelIndex = nextDefaultHitAreaIndexRef.current;
          nextDefaultHitAreaIndexRef.current = nextLabelIndex + 1;
          const nextId = `hit-area-${Date.now()}-${uniqueHitAreaSeedRef.current}`;
          uniqueHitAreaSeedRef.current += 1;

          setHitAreas((previousAreas) => {
            const nextHitArea = {
              id: nextId,
              name: getHitAreaDefaultName(nextLabelIndex),
              defaultLabelIndex: nextLabelIndex,
              lockBoolean: false,
              hideBoolean: false,
              ...nextImageRect,
              targetLink: "",
              timelineStart: Number(imageTimelineStart.toFixed(2)),
              timelineEnd: Number(imageTimelineEnd.toFixed(2)),
              keyframes: [],
            };
            return [nextHitArea, ...previousAreas];
          });

          setSelectedHitAreaId(nextId);
          setSelectedKeyframeId(null);
          snapTimelineMarkerToStart();
          hasCreatedHitArea = true;
        }
      }

      setDraftRect(null);
      draftRectRef.current = null;
      if (hasCreatedHitArea) {
        queueHistoryCommit();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    currentTimelineSecond,
    displayToImageRect,
    constrainDisplayRectToNonOverlap,
    getPointWithinStage,
    hitAreas,
    imageTimelineEnd,
    imageTimelineStart,
    isHitAreaLimitReached,
    isDrawing,
    queueHistoryCommit,
    snapTimelineMarkerToStart,
  ]);

  // If draw mode is disabled, clear transient draw/transform feedback immediately.
  useEffect(() => {
    if (isDrawToolActive) return;
    if (isDrawing) {
      setIsDrawing(false);
      setDraftRect(null);
      draftRectRef.current = null;
    }
    isHitAreaTransformingRef.current = false;
    setSmartFeedback(null);
  }, [isDrawToolActive, isDrawing]);

  const timelineScale = useMemo(
    () => ({ divisions: safeDuration, width: safeTimelineWidth }),
    [safeDuration, safeTimelineWidth]
  );

  const modalCanvasAppData = useMemo(
    () => ({
      genericInfo: { draggerWidth: restoredDraggerWidth },
      updateGenericInfo: (next) => {
        const nextDraggerWidth = toNumber(next?.draggerWidth, NaN);
        if (!Number.isFinite(nextDraggerWidth) || nextDraggerWidth <= 0) {
          return;
        }
        setDraggerWidth(clamp(nextDraggerWidth, 45, maxDraggerWidth));
      },
    }),
    [restoredDraggerWidth, maxDraggerWidth]
  );

  const resolveTimelineRange = useCallback(
    (item) => {
      const timelineStart = clamp(
        toNumber(item?.timelineStart, imageTimelineStart),
        imageTimelineStart,
        imageTimelineEnd - MIN_TIMELINE_DURATION
      );
      const timelineEndFallback = timelineStart + imageActiveTimelineDuration;
      const timelineEnd = clamp(
        toNumber(item?.timelineEnd, timelineEndFallback),
        timelineStart + MIN_TIMELINE_DURATION,
        imageTimelineEnd
      );
      return { timelineStart, timelineEnd };
    },
    [imageActiveTimelineDuration, imageTimelineEnd, imageTimelineStart]
  );

  // Project all hit areas into timeline rows plus the parent image summary row.
  const timelineRows = useMemo(() => {
    const imageClickableAreaCount = getConfiguredClickableAreaCount(hitAreas);

    const hitAreaRows = hitAreas.map((item, index) => {
      const { timelineStart, timelineEnd } = resolveTimelineRange(item);
      const keyframes = sortKeyframesByTime(
        sanitizeHitAreaKeyframes(
          { keyframes: item.keyframes || [] },
          timelineStart,
          timelineEnd,
          item.width,
          item.height
        )
      );
      return {
        id: item.id,
        name: resolveHitAreaDisplayName(item, index + 1),
        type: "hit-area",
        isLocked: !!item.lockBoolean,
        isHidden: !!item.hideBoolean,
        isSelected: !isTimelinePlaying && selectedHitAreaId === item.id,
        isBoundaryWarning: boundaryWarningIds.has(item.id),
        timelineStart,
        timelineEnd,
        keyframes,
        selectedKeyframeId:
          !isTimelinePlaying && selectedHitAreaId === item.id
            ? selectedKeyframeId
            : null,
        hasLinkIndicator: false,
        linkTooltip: "",
      };
    });

    return [
      ...hitAreaRows,
      {
        id: "image-row",
        name: layerName,
        type: "image",
        isLocked: imageLayerLocked,
        isHidden: imageLayerHidden,
        isSelected: !isTimelinePlaying && selectedHitAreaId === null,
        isBoundaryWarning: false,
        timelineStart: imageTimelineStart,
        timelineEnd: imageTimelineEnd,
        hasLinkIndicator: imageClickableAreaCount > 0,
        linkTooltip: getClickableAreasConfiguredTooltip(imageClickableAreaCount),
      },
    ];
  }, [
    boundaryWarningIds,
    hitAreas,
    imageLayerHidden,
    imageTimelineEnd,
    imageTimelineStart,
    imageLayerLocked,
    layerName,
    isTimelinePlaying,
    resolveTimelineRange,
    selectedKeyframeId,
    selectedHitAreaId,
  ]);

  const updateTimelineRangeById = useCallback(
    (id, start, end, options = {}) => {
      const shiftKeyframesBy = toNumber(options.shiftKeyframesBy, 0);
      const preserveKeyframes = !!options.preserveKeyframes;
      const nextStart = roundTimelineTime(start);
      const nextEnd = roundTimelineTime(end);
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== id) return item;
          if (item?.lockBoolean) return item;
          if (preserveKeyframes) {
            return {
              ...item,
              timelineStart: nextStart,
              timelineEnd: nextEnd,
            };
          }
          const shiftedKeyframes = cloneHitAreaKeyframes(item.keyframes || []).map(
            (frame) => ({
              ...frame,
              time:
                Math.abs(shiftKeyframesBy) > 0
                  ? roundTimelineTime(frame.time + shiftKeyframesBy)
                  : frame.time,
            })
          );
          const normalizedKeyframes = clipKeyframesToRange(
            shiftedKeyframes,
            nextStart,
            nextEnd
          );
          const baseFrame = normalizedKeyframes[0];
          return {
            ...item,
            timelineStart: nextStart,
            timelineEnd: nextEnd,
            width: roundDimension(baseFrame?.width ?? item.width),
            height: roundDimension(baseFrame?.height ?? item.height),
            keyframes: normalizedKeyframes,
          };
        })
      );
    },
    []
  );

  const updateTimelineKeyframeTimeById = useCallback(
    (hitAreaId, keyframeId, nextTimeInSeconds) => {
      const normalizedTime = roundTimelineTime(nextTimeInSeconds);
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== hitAreaId) return item;
          if (item?.lockBoolean) return item;
          const currentKeyframes = cloneHitAreaKeyframes(item.keyframes || []);
          let hasChanged = false;
          const nextKeyframes = sortKeyframesByTime(
            currentKeyframes.map((frame) => {
              if (String(frame.id) !== String(keyframeId)) return frame;
              const frameTime = roundTimelineTime(frame.time);
              if (frameTime === normalizedTime) return frame;
              hasChanged = true;
              return {
                ...frame,
                time: normalizedTime,
              };
            })
          );
          if (!hasChanged) return item;
          const baseFrame = nextKeyframes[0];
          return {
            ...item,
            width: roundDimension(baseFrame?.width ?? item.width),
            height: roundDimension(baseFrame?.height ?? item.height),
            keyframes: nextKeyframes,
          };
        })
      );
    },
    []
  );

  const beginTimelineKeyframeDrag = useCallback(
    (event, row, keyframe) => {
      if (isTimelinePlaying) return;
      if (row.type !== "hit-area") return;
      if (row?.isLocked) return;
      event.preventDefault();
      event.stopPropagation();
      if (!dismissKeyframePopupForSelectionChange()) return;
      stopTimelinePlayback();
      const isSwitchingLayer =
        String(selectedHitAreaId || "") !== String(row.id || "");
      setSelectedHitAreaId(row.id);
      if (isSwitchingLayer) {
        snapTimelineMarkerToStart();
      }
      setSelectedKeyframeId(keyframe.id);
      keyframeDragMovedRef.current = false;
      suppressNextKeyframeClickRef.current = false;
      setActiveKeyframeDrag({
        hitAreaId: row.id,
        keyframeId: keyframe.id,
        pointerStartX: event.clientX,
        initialTime: roundTimelineTime(keyframe.time),
        timelineStart: roundTimelineTime(row.timelineStart),
        timelineEnd: roundTimelineTime(row.timelineEnd),
      });
    },
    [
      dismissKeyframePopupForSelectionChange,
      isTimelinePlaying,
      selectedHitAreaId,
      snapTimelineMarkerToStart,
      stopTimelinePlayback,
    ]
  );

  const handleTimelineRowClick = (row) => {
    if (isTimelinePlaying) return;
    if (!dismissKeyframePopupForSelectionChange()) return;

    const nextSelectedId = row.type === "hit-area" ? row.id : null;
    const isSwitchingLayer =
      String(selectedHitAreaId || "") !== String(nextSelectedId || "");

    if (row.type === "hit-area") {
      setSelectedHitAreaId(row.id);
      if (isSwitchingLayer) {
        snapTimelineMarkerToStart();
      }
      const exactFrame = getExactKeyframeAtTime(
        row.keyframes || [],
        isSwitchingLayer ? 0 : currentTimelineSecond
      );
      setSelectedKeyframeId(exactFrame?.id || null);
    } else {
      setSelectedHitAreaId(null);
      setSelectedKeyframeId(null);
      if (isSwitchingLayer) {
        snapTimelineMarkerToStart();
      }
    }
  };

  const beginHitAreaRename = useCallback((event, row) => {
    if (isTimelinePlaying) return;
    if (row.type !== "hit-area") return;
    if (row?.isLocked) return;
    event.stopPropagation();
    if (!dismissKeyframePopupForSelectionChange()) return;
    setSelectedHitAreaId(row.id);
    const exactFrame = getExactKeyframeAtTime(
      row.keyframes || [],
      currentTimelineSecond
    );
    setSelectedKeyframeId(exactFrame?.id || null);
    setEditingHitAreaId(row.id);
    setEditingHitAreaName(String(row.name || ""));
  }, [currentTimelineSecond, dismissKeyframePopupForSelectionChange, isTimelinePlaying]);

  const finishHitAreaRename = useCallback(
    (shouldSubmit) => {
      if (!editingHitAreaId) return;

      if (!shouldSubmit) {
        setEditingHitAreaId(null);
        setEditingHitAreaName("");
        return;
      }

      const validation = validateHitAreaRename({
        rawName: editingHitAreaName,
        existingAreas: hitAreas,
        editingHitAreaId,
        maxLength: MAX_HIT_AREA_NAME_LENGTH,
      });

      if (!validation.isValid) {
        if (validation.reason === "duplicate") {
          toast(
            <div className="notificationText">Hit area name already exists</div>,
            {
              type: "error",
              toastId: HIT_AREA_DUPLICATE_NAME_TOAST_ID,
              autoClose: HIT_AREA_DUPLICATE_NAME_TOAST_MS,
            }
          );
          return;
        }
        setEditingHitAreaId(null);
        setEditingHitAreaName("");
        return;
      }

      updateHitAreaById(editingHitAreaId, { name: validation.normalizedName });
      setEditingHitAreaId(null);
      setEditingHitAreaName("");
      queueHistoryCommit();
    },
    [
      editingHitAreaId,
      editingHitAreaName,
      hitAreas,
      queueHistoryCommit,
      updateHitAreaById,
    ]
  );

  const handleHitAreaRenameInputChange = useCallback((nextValue) => {
    setEditingHitAreaName(sanitizeHitAreaRenameInput(nextValue));
  }, []);

  const syncTimelineVerticalScroll = useCallback((sourceRef, targetRef) => {
    if (timelineScrollSyncRef.current) return;
    const sourceElement = sourceRef.current;
    const targetElement = targetRef.current;
    if (!sourceElement || !targetElement) return;

    timelineScrollSyncRef.current = true;
    targetElement.scrollTop = sourceElement.scrollTop;
    timelineScrollSyncRef.current = false;
  }, []);

  const handleTimelineLeftScroll = useCallback(() => {
    syncTimelineVerticalScroll(timelineLeftColumnRef, timelineRightColumnRef);
  }, [syncTimelineVerticalScroll]);

  const handleTimelineRightScroll = useCallback(() => {
    syncTimelineVerticalScroll(timelineRightColumnRef, timelineLeftColumnRef);
  }, [syncTimelineVerticalScroll]);

  const beginTimelineRangeEdit = (event, row, mode) => {
    if (isTimelinePlaying) return;
    if (row.type !== "hit-area") return;
    if (row?.isLocked) return;
    // Only allow duration resize via the end handle — not free horizontal drag.
    if (mode === "move" || mode === "resize-start") return;
    event.stopPropagation();
    event.preventDefault();
    if (!dismissKeyframePopupForSelectionChange()) return;
    stopTimelinePlayback();

    const isSwitchingLayer =
      String(selectedHitAreaId || "") !== String(row.id || "");
    setSelectedHitAreaId(row.id);
    if (isSwitchingLayer) {
      snapTimelineMarkerToStart();
    }
    const exactFrame = getExactKeyframeAtTime(
      row.keyframes || [],
      isSwitchingLayer ? 0 : currentTimelineSecond
    );
    setSelectedKeyframeId(exactFrame?.id || null);
    timelineRangeDragMovedRef.current = false;
    setActiveTimelineEdit({
      id: row.id,
      mode,
      pointerStartX: event.clientX,
      initialStart: row.timelineStart,
      initialEnd: row.timelineEnd,
      initialDuration: row.timelineEnd - row.timelineStart,
    });
  };

  useEffect(() => {
    if (!activeTimelineEdit) return;

    const handleMouseMove = (event) => {
      const deltaPx = event.clientX - activeTimelineEdit.pointerStartX;
      if (Math.abs(deltaPx) >= KEYFRAME_DRAG_START_THRESHOLD_PX) {
        timelineRangeDragMovedRef.current = true;
      }
      const deltaSeconds = secondWidth > 0 ? deltaPx / secondWidth : 0;
      const currentStart = activeTimelineEdit.initialStart;
      const currentEnd = activeTimelineEdit.initialEnd;
      const minRangeStart = imageTimelineStart;
      const maxRangeEnd = imageTimelineEnd;
      const editedArea = hitAreasRef.current.find(
        (item) => item.id === activeTimelineEdit.id
      );
      const hasKeyframes = (editedArea?.keyframes || []).length > 0;

      if (activeTimelineEdit.mode === "move") {
        const nextStart = clamp(
          currentStart + deltaSeconds,
          minRangeStart,
          Math.max(
            minRangeStart,
            maxRangeEnd - activeTimelineEdit.initialDuration
          )
        );
        const nextEnd = clamp(
          nextStart + activeTimelineEdit.initialDuration,
          nextStart + MIN_TIMELINE_DURATION,
          maxRangeEnd
        );
        updateTimelineRangeById(activeTimelineEdit.id, nextStart, nextEnd, {
          shiftKeyframesBy: nextStart - currentStart,
          preserveKeyframes: !hasKeyframes,
        });
        return;
      }

      if (activeTimelineEdit.mode === "resize-start") {
        const nextStart = clamp(
          currentStart + deltaSeconds,
          minRangeStart,
          currentEnd - MIN_TIMELINE_DURATION
        );
        updateTimelineRangeById(activeTimelineEdit.id, nextStart, currentEnd, {
          preserveKeyframes: true,
        });
        return;
      }

      if (activeTimelineEdit.mode === "resize-end") {
        const nextEnd = clamp(
          currentEnd + deltaSeconds,
          currentStart + MIN_TIMELINE_DURATION,
          maxRangeEnd
        );
        updateTimelineRangeById(activeTimelineEdit.id, currentStart, nextEnd, {
          preserveKeyframes: true,
        });
      }
    };

    const handleMouseUp = () => {
      const hasMoved = timelineRangeDragMovedRef.current;
      timelineRangeDragMovedRef.current = false;
      const wasRangeResize =
        activeTimelineEdit.mode === "resize-start" ||
        activeTimelineEdit.mode === "resize-end";

      if (wasRangeResize) {
        const editedArea = hitAreasRef.current.find(
          (item) => item.id === activeTimelineEdit.id
        );
        if (editedArea) {
          const { timelineStart, timelineEnd } = resolveTimelineRange(editedArea);
          // Drop keyframes that fall outside the resized range (no confirm).
          updateTimelineRangeById(activeTimelineEdit.id, timelineStart, timelineEnd);
        }
      }

      if (hasMoved) {
        suppressNextKeyframeClickRef.current = true;
        // After resizing a hit-area layer bar, reset the playhead to 0s.
        if (wasRangeResize) {
          snapTimelineMarkerToStart();
          setSelectedKeyframeId(null);
        }
      }
      setActiveTimelineEdit(null);
      queueHistoryCommit();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    activeTimelineEdit,
    imageTimelineEnd,
    imageTimelineStart,
    queueHistoryCommit,
    resolveTimelineRange,
    secondWidth,
    snapTimelineMarkerToStart,
    updateTimelineRangeById,
  ]);

  useEffect(() => {
    if (!activeKeyframeDrag) return undefined;

    const handleMouseMove = (event) => {
      const deltaPx = event.clientX - activeKeyframeDrag.pointerStartX;
      if (Math.abs(deltaPx) >= KEYFRAME_DRAG_START_THRESHOLD_PX) {
        keyframeDragMovedRef.current = true;
      }
      if (secondWidth <= 0) return;
      const deltaSeconds = deltaPx / secondWidth;
      const intendedTime = clamp(
        activeKeyframeDrag.initialTime + deltaSeconds,
        activeKeyframeDrag.timelineStart,
        activeKeyframeDrag.timelineEnd
      );
      const targetArea = hitAreasRef.current.find(
        (item) => item.id === activeKeyframeDrag.hitAreaId
      );
      const constrainedTime = constrainKeyframeDragTime(intendedTime, {
        keyframes: targetArea?.keyframes || [],
        draggedKeyframeId: activeKeyframeDrag.keyframeId,
        initialTime: activeKeyframeDrag.initialTime,
        timelineStart: activeKeyframeDrag.timelineStart,
        timelineEnd: activeKeyframeDrag.timelineEnd,
      });
      updateTimelineKeyframeTimeById(
        activeKeyframeDrag.hitAreaId,
        activeKeyframeDrag.keyframeId,
        constrainedTime
      );
    };

    const handleMouseUp = (event) => {
      const dragSnapshot = activeKeyframeDrag;
      const hasMoved = keyframeDragMovedRef.current;
      setActiveKeyframeDrag(null);
      keyframeDragMovedRef.current = false;
      if (hasMoved) {
        suppressNextKeyframeClickRef.current = true;
        if (dragSnapshot && secondWidth > 0) {
          const deltaPx = event.clientX - dragSnapshot.pointerStartX;
          const intendedTime = roundTimelineTime(
            clamp(
              dragSnapshot.initialTime + deltaPx / secondWidth,
              dragSnapshot.timelineStart,
              dragSnapshot.timelineEnd
            )
          );
          const targetArea = hitAreasRef.current.find(
            (item) => item.id === dragSnapshot.hitAreaId
          );
          const constrainedTime = constrainKeyframeDragTime(intendedTime, {
            keyframes: targetArea?.keyframes || [],
            draggedKeyframeId: dragSnapshot.keyframeId,
            initialTime: dragSnapshot.initialTime,
            timelineStart: dragSnapshot.timelineStart,
            timelineEnd: dragSnapshot.timelineEnd,
          });
          updateTimelineKeyframeTimeById(
            dragSnapshot.hitAreaId,
            dragSnapshot.keyframeId,
            constrainedTime
          );
        }
        queueHistoryCommit();
      }
    };

    window.addEventListener("mousemove", handleMouseMove, true);
    window.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("blur", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove, true);
      window.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("blur", handleMouseUp);
    };
  }, [
    activeKeyframeDrag,
    queueHistoryCommit,
    secondWidth,
    updateTimelineKeyframeTimeById,
  ]);

  const maxTimelineMarkerLeft = Math.max(
    0,
    draggerWidth
  );

  const timelineMarkerLeft = useMemo(
    () =>
      clamp(currentTimelineSecond * secondWidth, 0, safeTimelineWidth),
    [currentTimelineSecond, secondWidth, safeTimelineWidth]
  );

  useEffect(() => {
    if (!open || !isTimelinePlaying) return undefined;
    if (previewStageDimensions.width <= 0 || previewStageDimensions.height <= 0) {
      return undefined;
    }

    // Resume from the live playhead so incidental effect re-runs don't restart.
    const startTime = currentTimelineSecondRef.current;

    hitAreaPlaybackFrameRef.current = window.requestAnimationFrame(() => {
      // Seek into the paused time without clearing styles first — avoids a flash to 0.
      hitAreaPlaybackTimelineRef.current = createHitAreaModalPlaybackTimeline({
        hitAreas: hitAreasRef.current,
        imageWidth,
        imageHeight,
        stageWidth: previewStageDimensions.width,
        stageHeight: previewStageDimensions.height,
        startTime,
        totalDuration: visibleTimelineSeconds,
        onTimeUpdate: (nextTime) => {
          const clampedTime = clamp(
            roundTimelineTime(nextTime),
            0,
            visibleTimelineSeconds
          );
          currentTimelineSecondRef.current = clampedTime;
          if (clampedTime >= visibleTimelineSeconds - KEYFRAME_TIME_EPSILON) {
            snapTimelineMarkerToStart();
            stopTimelinePlayback();
            return;
          }
          setCurrentTimelineSecond(clampedTime);
        },
        onComplete: () => {
          snapTimelineMarkerToStart();
          stopTimelinePlayback();
        },
      });
    });

    return () => {
      if (hitAreaPlaybackFrameRef.current) {
        window.cancelAnimationFrame(hitAreaPlaybackFrameRef.current);
        hitAreaPlaybackFrameRef.current = null;
      }
      // Kill the GSAP timeline only — do not reset element styles here.
      // Soft pause must keep preview positions so resume continues from there.
      // Full reset is handled by stopTimelinePlayback (complete / close / cancel).
      if (hitAreaPlaybackTimelineRef.current) {
        try {
          hitAreaPlaybackTimelineRef.current.kill();
        } catch (e) {
          // already killed
        }
        hitAreaPlaybackTimelineRef.current = null;
      }
    };
  }, [
    imageHeight,
    imageWidth,
    isTimelinePlaying,
    open,
    previewStageDimensions.height,
    previewStageDimensions.width,
    visibleTimelineSeconds,
    snapTimelineMarkerToStart,
    stopTimelinePlayback,
  ]);

  useEffect(() => {
    if (!isTimelinePlaying) return;
    if (
      isDrawing ||
      !!activeTimelineEdit ||
      !!activeKeyframeDrag ||
      timelineMarkerDragRef.current ||
      isHitAreaTransformingRef.current
    ) {
      stopTimelinePlayback();
    }
  }, [
    activeKeyframeDrag,
    activeTimelineEdit,
    isDrawing,
    isTimelinePlaying,
    stopTimelinePlayback,
  ]);

  useEffect(() => {
    const maxVisible = roundTimelineTime(visibleTimelineSeconds);
    const previous = roundTimelineTime(currentTimelineSecondRef.current);
    // Timeline shrunk past the playhead — snap to 0s instead of dragging with the edge.
    if (previous > maxVisible) {
      snapTimelineMarkerToStart();
      return;
    }
    setCurrentTimelineSecond((previousValue) =>
      clamp(roundTimelineTime(previousValue), 0, maxVisible)
    );
  }, [safeDuration, visibleTimelineSeconds, snapTimelineMarkerToStart]);

  const updateTimelineSecondFromClientX = useCallback(
    (clientX) => {
      const timelineInnerElement = timelineRightInnerRef.current;
      if (!timelineInnerElement || timelinePixelsPerSecond <= 0) return null;

      // When a hit-area layer is selected, keep the playhead inside that
      // layer's active range (e.g. 0–7s if the bar ends at 7s).
      const selectedRange = selectedHitArea
        ? resolveTimelineRange(selectedHitArea)
        : null;
      const rangeMin = selectedRange
        ? clamp(
            roundTimelineTime(selectedRange.timelineStart),
            0,
            visibleTimelineSeconds
          )
        : 0;
      const rangeMax = selectedRange
        ? clamp(
            roundTimelineTime(selectedRange.timelineEnd),
            rangeMin,
            visibleTimelineSeconds
          )
        : visibleTimelineSeconds;

      const innerBounds = timelineInnerElement.getBoundingClientRect();
      const maxMarkerLeftByDragger = Math.max(
        0,
        draggerWidth - HIT_AREA_TIMELINE_MARKER_HEAD_OVERHANG_PX
      );
      const minMarkerLeftByRange = Math.max(
        0,
        rangeMin * timelinePixelsPerSecond
      );
      const maxMarkerLeftByRange = Math.max(
        minMarkerLeftByRange,
        rangeMax * timelinePixelsPerSecond
      );
      const maxMarkerLeft = Math.min(maxMarkerLeftByDragger, maxMarkerLeftByRange);
      const pointerOffset = clamp(
        clientX -
          innerBounds.left -
          HIT_AREA_TIMELINE_LEFT_INSET +
          timelineInnerElement.scrollLeft,
        minMarkerLeftByRange,
        maxMarkerLeft
      );
      const nextSecond = roundTimelineTime(
        clamp(
          pointerOffset / timelinePixelsPerSecond,
          rangeMin,
          rangeMax
        )
      );
      setCurrentTimelineSecond(nextSecond);
      if (timelineMarkerDragRef.current && hitAreaScrubTimelineRef.current) {
        scrubHitAreaModalTimeline(hitAreaScrubTimelineRef.current, nextSecond);
      }
      return nextSecond;
    },
    [
      draggerWidth,
      resolveTimelineRange,
      selectedHitArea,
      timelinePixelsPerSecond,
      visibleTimelineSeconds,
    ]
  );

  const beginTimelineMarkerDrag = useCallback(
    (event) => {
      if (isTimelinePlaying) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      stopTimelinePlayback();
      // Next Play should restart from 0 after scrubbing the marker.
      restartPlaybackFromStartRef.current = true;
      setKeyframePopupState((previousState) =>
        previousState.isOpen ? { ...previousState, isOpen: false } : previousState
      );
      setPendingKeyframeTime(null);
      setIsTimelineMarkerScrubbing(true);
      timelineMarkerDragRef.current = true;
      updateTimelineSecondFromClientX(event.clientX);
    },
    [isTimelinePlaying, stopTimelinePlayback, updateTimelineSecondFromClientX]
  );

  useEffect(() => {
    if (!open || !isTimelineMarkerScrubbing) return undefined;
    if (previewStageDimensions.width <= 0 || previewStageDimensions.height <= 0) {
      return undefined;
    }

    let cancelled = false;
    const frameId = window.requestAnimationFrame(() => {
      if (cancelled) return;
      killHitAreaModalTimeline(hitAreaScrubTimelineRef.current, hitAreasRef.current);
      hitAreaScrubTimelineRef.current = createHitAreaModalPlaybackTimeline({
        hitAreas: hitAreasRef.current,
        imageWidth,
        imageHeight,
        stageWidth: previewStageDimensions.width,
        stageHeight: previewStageDimensions.height,
        startTime: currentTimelineSecondRef.current,
        totalDuration: visibleTimelineSeconds,
        autoplay: false,
      });
      scrubHitAreaModalTimeline(
        hitAreaScrubTimelineRef.current,
        currentTimelineSecondRef.current
      );
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      killHitAreaModalTimeline(hitAreaScrubTimelineRef.current, hitAreasRef.current);
      hitAreaScrubTimelineRef.current = null;
    };
  }, [
    imageHeight,
    imageWidth,
    isTimelineMarkerScrubbing,
    open,
    previewStageDimensions.height,
    previewStageDimensions.width,
    visibleTimelineSeconds,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    const handleMouseMove = (event) => {
      if (!timelineMarkerDragRef.current) return;
      updateTimelineSecondFromClientX(event.clientX);
    };
    const handleMouseUp = () => {
      timelineMarkerDragRef.current = false;
      setIsTimelineMarkerScrubbing(false);
    };

    window.addEventListener("mousemove", handleMouseMove, true);
    window.addEventListener("mouseup", handleMouseUp, true);
    window.addEventListener("blur", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove, true);
      window.removeEventListener("mouseup", handleMouseUp, true);
      window.removeEventListener("blur", handleMouseUp);
      timelineMarkerDragRef.current = false;
    };
  }, [open, updateTimelineSecondFromClientX]);

  const selectedHitAreaTimelineRange = useMemo(() => {
    if (!selectedHitArea) return null;
    return resolveTimelineRange(selectedHitArea);
  }, [resolveTimelineRange, selectedHitArea]);

  const isPlayheadWithinSelectedHitAreaRange = useMemo(() => {
    if (!selectedHitAreaTimelineRange) return false;
    return isTimeWithinRange(
      roundTimelineTime(currentTimelineSecond),
      selectedHitAreaTimelineRange.timelineStart,
      selectedHitAreaTimelineRange.timelineEnd
    );
  }, [currentTimelineSecond, selectedHitAreaTimelineRange]);

  const canAddKeyframeAtPlayhead =
    !isTimelinePlaying &&
    !!selectedHitArea &&
    !isSelectedHitAreaLocked &&
    isPlayheadWithinSelectedHitAreaRange;
  const showAddKeyframeButton =
    !isTimelinePlaying && (selectedHitArea?.keyframes || []).length > 0;
  const isSelectedHitAreaKeyframeEditable =
    !!selectedHitArea && !isSelectedHitAreaLocked;

  const keyframeDimensionLimits = useMemo(() => {
    if (!selectedHitArea) {
      return { maxWidth: MIN_RECT_SIZE, maxHeight: MIN_RECT_SIZE };
    }
    return {
      maxWidth: Math.max(MIN_RECT_SIZE, imageWidth - roundToInt(selectedHitArea.x)),
      maxHeight: Math.max(MIN_RECT_SIZE, imageHeight - roundToInt(selectedHitArea.y)),
    };
  }, [imageHeight, imageWidth, selectedHitArea]);

  const keyframeEditorValidation = useMemo(() => {
    const rawWidth = Number(keyframeEditorWidth);
    const rawHeight = Number(keyframeEditorHeight);
    const nextErrors = { width: "", height: "" };

    if (!Number.isFinite(rawWidth)) {
      nextErrors.width = "Enter a valid width";
    } else if (rawWidth <= 0) {
      nextErrors.width = "Width must be greater than 0";
    } else if (rawWidth < MIN_RECT_SIZE) {
      nextErrors.width = `Minimum width is ${MIN_RECT_SIZE}px`;
    }

    if (!Number.isFinite(rawHeight)) {
      nextErrors.height = "Enter a valid height";
    } else if (rawHeight <= 0) {
      nextErrors.height = "Height must be greater than 0";
    } else if (rawHeight < MIN_RECT_SIZE) {
      nextErrors.height = `Minimum height is ${MIN_RECT_SIZE}px`;
    }

    return nextErrors;
  }, [keyframeEditorHeight, keyframeEditorWidth]);

  const isKeyframeEditorInvalid =
    !!keyframeEditorValidation.width || !!keyframeEditorValidation.height;

  const resolveKeyframeEditorDimensions = useCallback(
    (rawWidth, rawHeight) => {
      const baseRect = getCurrentSelectedHitAreaRect();
      if (!baseRect || !selectedHitArea) {
        return {
          width: rawWidth,
          height: rawHeight,
          numericWidth: null,
          numericHeight: null,
          wasAdjusted: false,
        };
      }

      const parsedWidth = Number(rawWidth);
      const parsedHeight = Number(rawHeight);
      if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) {
        return {
          width: rawWidth,
          height: rawHeight,
          numericWidth: null,
          numericHeight: null,
          wasAdjusted: false,
        };
      }

      if (parsedWidth < MIN_RECT_SIZE || parsedHeight < MIN_RECT_SIZE) {
        return {
          width: rawWidth,
          height: rawHeight,
          numericWidth: null,
          numericHeight: null,
          wasAdjusted: false,
        };
      }

      const keyframePreviewTime =
        pendingKeyframeTime ??
        selectedHitAreaKeyframe?.time ??
        currentTimelineSecond;
      const obstacles = getOtherHitAreaImageRectsAtTime(
        selectedHitArea.id,
        keyframePreviewTime
      );
      const imageMaxWidth = Math.max(
        MIN_RECT_SIZE,
        imageWidth - roundToInt(baseRect.x)
      );
      const imageMaxHeight = Math.max(
        MIN_RECT_SIZE,
        imageHeight - roundToInt(baseRect.y)
      );
      const targetWidth = clamp(
        roundDimension(parsedWidth),
        MIN_RECT_SIZE,
        imageMaxWidth
      );
      const targetHeight = clamp(
        roundDimension(parsedHeight),
        MIN_RECT_SIZE,
        imageMaxHeight
      );
      const constrainedRect = constrainRectSizeByInterpolation(
        { x: baseRect.x, y: baseRect.y },
        { width: baseRect.width, height: baseRect.height },
        { width: targetWidth, height: targetHeight },
        obstacles,
        imageWidth,
        imageHeight
      );
      const wasAdjusted =
        constrainedRect.width !== targetWidth ||
        constrainedRect.height !== targetHeight;

      return {
        width: formatDimensionForEditor(constrainedRect.width),
        height: formatDimensionForEditor(constrainedRect.height),
        numericWidth: constrainedRect.width,
        numericHeight: constrainedRect.height,
        wasAdjusted,
      };
    },
    [
      currentTimelineSecond,
      getCurrentSelectedHitAreaRect,
      getOtherHitAreaImageRectsAtTime,
      imageHeight,
      imageWidth,
      pendingKeyframeTime,
      selectedHitArea,
      selectedHitAreaKeyframe,
    ]
  );

  const handleKeyframeEditorWidthChange = useCallback(
    (rawValue) => {
      setKeyframeEditorErrors((previousState) => ({
        ...previousState,
        width: "",
      }));
      const clampedInput = clampKeyframeEditorDimensionInput(rawValue, {
        min: MIN_RECT_SIZE,
        max: keyframeDimensionLimits.maxWidth,
      });
      setKeyframeEditorWidth(clampedInput);

      if (
        clampedInput === "" ||
        clampedInput === "." ||
        String(clampedInput).endsWith(".")
      ) {
        return;
      }

      const resolved = resolveKeyframeEditorDimensions(
        clampedInput,
        keyframeEditorHeight
      );
      if (resolved.wasAdjusted) {
        setKeyframeEditorWidth(resolved.width);
        setKeyframeEditorHeight(resolved.height);
      }
    },
    [
      keyframeDimensionLimits.maxWidth,
      keyframeEditorHeight,
      resolveKeyframeEditorDimensions,
    ]
  );

  const handleKeyframeEditorHeightChange = useCallback(
    (rawValue) => {
      setKeyframeEditorErrors((previousState) => ({
        ...previousState,
        height: "",
      }));
      const clampedInput = clampKeyframeEditorDimensionInput(rawValue, {
        min: MIN_RECT_SIZE,
        max: keyframeDimensionLimits.maxHeight,
      });
      setKeyframeEditorHeight(clampedInput);

      if (
        clampedInput === "" ||
        clampedInput === "." ||
        String(clampedInput).endsWith(".")
      ) {
        return;
      }

      const resolved = resolveKeyframeEditorDimensions(
        keyframeEditorWidth,
        clampedInput
      );
      if (resolved.wasAdjusted) {
        setKeyframeEditorWidth(resolved.width);
        setKeyframeEditorHeight(resolved.height);
      }
    },
    [
      keyframeDimensionLimits.maxHeight,
      keyframeEditorWidth,
      resolveKeyframeEditorDimensions,
    ]
  );

  const hasUnsavedKeyframeEditorChanges = useMemo(() => {
    if (!selectedHitAreaKeyframe || !keyframePopupState.isOpen) return false;
    const parsedWidth = Number(keyframeEditorWidth);
    const parsedHeight = Number(keyframeEditorHeight);
    if (!Number.isFinite(parsedWidth) || !Number.isFinite(parsedHeight)) {
      return true;
    }
    return (
      roundDimension(parsedWidth) !== roundDimension(selectedHitAreaKeyframe.width) ||
      roundDimension(parsedHeight) !== roundDimension(selectedHitAreaKeyframe.height)
    );
  }, [
    keyframeEditorHeight,
    keyframeEditorWidth,
    keyframePopupState.isOpen,
    selectedHitAreaKeyframe,
  ]);

  const confirmDiscardKeyframePopupChanges = useCallback(() => {
    if (!keyframePopupState.isOpen || !hasUnsavedKeyframeEditorChanges) return true;
    // Keep Update disabled for invalid values; allow switching without confirm.
    if (isKeyframeEditorInvalid) return true;
    return window.confirm(
      "You have unsaved keyframe changes. Discard and open another keyframe?"
    );
  }, [
    hasUnsavedKeyframeEditorChanges,
    isKeyframeEditorInvalid,
    keyframePopupState.isOpen,
  ]);

  const beginAddKeyframeAtTime = useCallback(
    (hitAreaId, keyframeTime, markerElement, options = {}) => {
      const preserveTimelineSecond = !!options.preserveTimelineSecond;
      const hitArea = hitAreas.find((item) => item.id === hitAreaId) || null;
      if (!hitArea || hitArea.lockBoolean) {
        showHitAreaComponentLockedToast();
        return false;
      }

      const { timelineStart, timelineEnd } = resolveTimelineRange(hitArea);
      const normalizedTime = roundTimelineTime(keyframeTime);
      if (!isTimeWithinRange(normalizedTime, timelineStart, timelineEnd)) {
        toast.error(
          <div className="notificationText">
            Keyframes can only be added within the hit area active range
          </div>
        );
        return false;
      }

      const existingKeyframes = sortKeyframesByTime(
        cloneHitAreaKeyframes(hitArea.keyframes || [])
      );
      const existingFrameAtTime = getExactKeyframeAtTime(
        existingKeyframes,
        normalizedTime
      );

      setSelectedHitAreaId(hitAreaId);
      if (!preserveTimelineSecond) {
        setCurrentTimelineSecond(normalizedTime);
      }

      if (existingFrameAtTime) {
        toast.error(
          <div className="notificationText">
            A keyframe already exists at this time
          </div>
        );
        return false;
      }

      setPendingKeyframeTime(normalizedTime);
      setSelectedKeyframeId(null);
      syncKeyframeEditorFromHitAreaRect(hitArea);
      openKeyframePopupFromMarkerElement(markerElement);
      return true;
    },
    [
      hitAreas,
      openKeyframePopupFromMarkerElement,
      resolveTimelineRange,
      syncKeyframeEditorFromHitAreaRect,
    ]
  );

  // Add a keyframe at playhead with inherited dimensions and duplicate-time guards.
  const addKeyframeAtCurrentTimelineSecond = useCallback(
    (markerElement) => {
      if (!selectedHitArea || !selectedHitAreaTimelineRange) return;
      if (selectedHitArea?.lockBoolean) {
        showHitAreaComponentLockedToast();
        return;
      }
      if (!confirmDiscardKeyframePopupChanges()) return;
      if (
        !isTimeWithinRange(
          roundTimelineTime(currentTimelineSecond),
          selectedHitAreaTimelineRange.timelineStart,
          selectedHitAreaTimelineRange.timelineEnd
        )
      ) {
        toast.error(
          <div className="notificationText">
            Keyframes can only be added within the hit area active range
          </div>
        );
        return;
      }

      beginAddKeyframeAtTime(
        selectedHitArea.id,
        currentTimelineSecond,
        markerElement
      );
    },
    [
      beginAddKeyframeAtTime,
      confirmDiscardKeyframePopupChanges,
      currentTimelineSecond,
      selectedHitArea,
      selectedHitAreaTimelineRange,
    ]
  );

  const selectTimelineKeyframe = useCallback((event, row, keyframe) => {
    event.preventDefault();
    event.stopPropagation();
    if (isTimelinePlaying) return;
    if (suppressNextKeyframeClickRef.current) {
      suppressNextKeyframeClickRef.current = false;
      return;
    }
    const isSwitchingKeyframe =
      String(selectedHitAreaId || "") !== String(row.id || "") ||
      String(selectedKeyframeId || "") !== String(keyframe.id || "");
    if (isSwitchingKeyframe && !confirmDiscardKeyframePopupChanges()) {
      return;
    }
    if (row?.isLocked) {
      showHitAreaComponentLockedToast();
      return;
    }
    setSelectedHitAreaId(row.id);
    setSelectedKeyframeId(keyframe.id);
    const hitArea = hitAreas.find((item) => item.id === row.id) || null;
    const syncedHitArea = syncHitAreaLiveFromKeyframe(hitArea, keyframe);
    if (syncedHitArea) {
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => (item.id === row.id ? syncedHitArea : item))
      );
      syncKeyframeEditorFromHitAreaRect(syncedHitArea);
    }
    openKeyframePopupFromMarkerElement(event.currentTarget);
  }, [
    confirmDiscardKeyframePopupChanges,
    hitAreas,
    isTimelinePlaying,
    openKeyframePopupFromMarkerElement,
    selectedHitAreaId,
    selectedKeyframeId,
    syncHitAreaLiveFromKeyframe,
    syncKeyframeEditorFromHitAreaRect,
  ]);

  const openKeyframePopupFromTimelineEventMarker = useCallback(
    (event, row, markerType = "start") => {
      if (isTimelinePlaying) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!row || row.type !== "hit-area") return;
      event.preventDefault();
      event.stopPropagation();
      if (suppressNextKeyframeClickRef.current) {
        suppressNextKeyframeClickRef.current = false;
        return;
      }
      if (row.isLocked) {
        showHitAreaComponentLockedToast();
        return;
      }

      const orderedKeyframes = sortKeyframesByTime(
        cloneHitAreaKeyframes(row.keyframes || [])
      );

      if (!orderedKeyframes.length) {
        if (!confirmDiscardKeyframePopupChanges()) return;
        const playheadTime = roundTimelineTime(currentTimelineSecond);
        if (!isTimeWithinRange(playheadTime, row.timelineStart, row.timelineEnd)) {
          toast.error(
            <div className="notificationText">
              Keyframes can only be added within the hit area active range
            </div>
          );
          return;
        }
        beginAddKeyframeAtTime(row.id, playheadTime, event.currentTarget, {
          preserveTimelineSecond: true,
        });
        return;
      }

      const startFrame =
        orderedKeyframes.find(
          (frame) =>
            Math.abs(
              roundTimelineTime(frame.time) - roundTimelineTime(row.timelineStart)
            ) <= KEYFRAME_TIME_EPSILON
        ) || orderedKeyframes[0];
      const endFrame =
        getKeyframeAtTime(orderedKeyframes, row.timelineEnd) ||
        orderedKeyframes[orderedKeyframes.length - 1];
      const targetFrame = markerType === "end" ? endFrame : startFrame;
      if (!targetFrame) return;

      const isSwitchingKeyframe =
        String(selectedHitAreaId || "") !== String(row.id || "") ||
        String(selectedKeyframeId || "") !== String(targetFrame.id || "");
      if (isSwitchingKeyframe && !confirmDiscardKeyframePopupChanges()) {
        return;
      }

      setSelectedHitAreaId(row.id);
      setSelectedKeyframeId(targetFrame.id);
      setPendingKeyframeTime(null);
      const hitArea = hitAreas.find((item) => item.id === row.id) || null;
      const syncedHitArea = syncHitAreaLiveFromKeyframe(hitArea, targetFrame);
      if (syncedHitArea) {
        setHitAreas((previousAreas) =>
          previousAreas.map((item) => (item.id === row.id ? syncedHitArea : item))
        );
        syncKeyframeEditorFromHitAreaRect(syncedHitArea);
      }
      openKeyframePopupFromMarkerElement(event.currentTarget);
    },
    [
      beginAddKeyframeAtTime,
      confirmDiscardKeyframePopupChanges,
      currentTimelineSecond,
      hitAreas,
      isTimelinePlaying,
      openKeyframePopupFromMarkerElement,
      selectedHitAreaId,
      selectedKeyframeId,
      syncHitAreaLiveFromKeyframe,
      syncKeyframeEditorFromHitAreaRect,
    ]
  );

  const copySelectedKeyframeDimensions = useCallback(() => {
    if (!selectedHitAreaKeyframe || !selectedHitArea?.id) return;
    setKeyframeClipboard({
      hitAreaId: selectedHitArea.id,
      keyframeId: selectedHitAreaKeyframe.id,
      width: roundDimension(selectedHitAreaKeyframe.width),
      height: roundDimension(selectedHitAreaKeyframe.height),
    });
    toast.success(
      <div className="notificationText d-flex">Keyframe copied successfully</div>
    );
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
  }, [selectedHitArea?.id, selectedHitAreaKeyframe]);

  const pasteSelectedKeyframeDimensions = useCallback(() => {
    if (!keyframeClipboard) {
      toast.error(
        <div className="notificationText">No keyframe is copied</div>
      );
      return;
    }
    if (!selectedHitArea) return;

    const pastedWidth = roundDimension(keyframeClipboard.width);
    const pastedHeight = roundDimension(keyframeClipboard.height);
    const safeWidth = clamp(
      pastedWidth,
      MIN_RECT_SIZE,
      keyframeDimensionLimits.maxWidth
    );
    const safeHeight = clamp(
      pastedHeight,
      MIN_RECT_SIZE,
      keyframeDimensionLimits.maxHeight
    );
    const constrainedDimensions = resolveKeyframeEditorDimensions(
      safeWidth,
      safeHeight
    );
    const resolvedWidth = Number.isFinite(constrainedDimensions.numericWidth)
      ? constrainedDimensions.numericWidth
      : safeWidth;
    const resolvedHeight = Number.isFinite(constrainedDimensions.numericHeight)
      ? constrainedDimensions.numericHeight
      : safeHeight;

    setKeyframeEditorWidth(formatDimensionForEditor(resolvedWidth));
    setKeyframeEditorHeight(formatDimensionForEditor(resolvedHeight));
    setKeyframeEditorErrors({ width: "", height: "" });

    const isAddingNew = pendingKeyframeTimeRef.current !== null;
    if (
      !isAddingNew &&
      selectedHitAreaKeyframe &&
      !selectedHitArea?.lockBoolean
    ) {
      const currentPositionRect = getCurrentSelectedHitAreaRect();
      if (!currentPositionRect) return;

      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== selectedHitArea.id) return item;
          const nextKeyframes = cloneHitAreaKeyframes(item.keyframes || []).map(
            (frame) =>
              String(frame.id) === String(selectedHitAreaKeyframe.id)
                ? {
                    ...frame,
                    width: resolvedWidth,
                    height: resolvedHeight,
                  }
                : frame
          );
          const baseFrame = sortKeyframesByTime(nextKeyframes)[0];
          return {
            ...item,
            width: roundDimension(baseFrame?.width ?? item.width),
            height: roundDimension(baseFrame?.height ?? item.height),
            keyframes: nextKeyframes,
          };
        })
      );
      queueHistoryCommit();
    }

    setKeyframeClipboard(null);
    toast.success(
      <div className="notificationText d-flex">Keyframe pasted successfully</div>
    );
  }, [
    getCurrentSelectedHitAreaRect,
    keyframeClipboard,
    keyframeDimensionLimits.maxHeight,
    keyframeDimensionLimits.maxWidth,
    queueHistoryCommit,
    resolveKeyframeEditorDimensions,
    selectedHitArea,
    selectedHitAreaKeyframe,
  ]);

  const updateSelectedKeyframeDimensions = useCallback(() => {
    const pendingAddTime = pendingKeyframeTimeRef.current;
    const isAddingNew = pendingAddTime !== null;
    const editingKeyframeId = selectedKeyframeIdRef.current;

    if (!selectedHitArea) return;
    if (selectedHitArea?.lockBoolean) return;

    if (!isAddingNew) {
      if (!editingKeyframeId) return;
      const liveHitArea =
        hitAreasRef.current.find((item) => item.id === selectedHitArea.id) ||
        selectedHitArea;
      const editingFrame = (liveHitArea.keyframes || []).find(
        (frame) => String(frame.id) === String(editingKeyframeId)
      );
      if (!editingFrame) return;
    }

    const resolvedTime = isAddingNew
      ? pendingAddTime
      : (
          hitAreasRef.current
            .find((item) => item.id === selectedHitArea.id)
            ?.keyframes || []
        ).find((frame) => String(frame.id) === String(editingKeyframeId))?.time;
    const currentPositionRect = getCurrentSelectedHitAreaRect();
    if (!currentPositionRect || resolvedTime === undefined) return;

    const parsedEditorWidth = Number(keyframeEditorWidth);
    const parsedEditorHeight = Number(keyframeEditorHeight);
    const widthSource = Number.isFinite(parsedEditorWidth)
      ? parsedEditorWidth
      : currentPositionRect.width;
    const heightSource = Number.isFinite(parsedEditorHeight)
      ? parsedEditorHeight
      : currentPositionRect.height;

    let safeWidth = clamp(
      roundDimension(widthSource),
      MIN_RECT_SIZE,
      keyframeDimensionLimits.maxWidth
    );
    let safeHeight = clamp(
      roundDimension(heightSource),
      MIN_RECT_SIZE,
      keyframeDimensionLimits.maxHeight
    );
    setKeyframeEditorErrors({ width: "", height: "" });

    const imageBoundedWidth = safeWidth;
    const imageBoundedHeight = safeHeight;
    const constrainedDimensions = resolveKeyframeEditorDimensions(
      safeWidth,
      safeHeight
    );
    if (
      Number.isFinite(constrainedDimensions.numericWidth) &&
      Number.isFinite(constrainedDimensions.numericHeight)
    ) {
      safeWidth = constrainedDimensions.numericWidth;
      safeHeight = constrainedDimensions.numericHeight;
    }

    const wasClampedToImageBounds =
      imageBoundedWidth !== roundDimension(widthSource) ||
      imageBoundedHeight !== roundDimension(heightSource);
    const wasClampedToHitAreas = constrainedDimensions.wasAdjusted;
    if (wasClampedToImageBounds || wasClampedToHitAreas) {
      toast.warning(
        <div className="notificationText">
          {wasClampedToHitAreas
            ? "Value exceeded adjacent hit area bounds and was adjusted"
            : "Value exceeded image bounds and was adjusted"}
        </div>
      );
      setKeyframeEditorWidth(formatDimensionForEditor(safeWidth));
      setKeyframeEditorHeight(formatDimensionForEditor(safeHeight));
    }

    const keyframeRect = {
      x: roundToInt(currentPositionRect.x),
      y: roundToInt(currentPositionRect.y),
      width: safeWidth,
      height: safeHeight,
    };

    if (isAddingNew) {
      const liveHitArea =
        hitAreasRef.current.find((item) => item.id === selectedHitArea.id) ||
        selectedHitArea;
      const { timelineStart, timelineEnd } = resolveTimelineRange(liveHitArea);
      if (!isTimeWithinRange(pendingAddTime, timelineStart, timelineEnd)) {
        toast.error(
          <div className="notificationText">
            Keyframes can only be added within the hit area active range
          </div>
        );
        setPendingKeyframeTime(null);
        setKeyframePopupState((prev) => ({ ...prev, isOpen: false }));
        return;
      }
      const currentKeyframes = cloneHitAreaKeyframes(liveHitArea.keyframes || []);
      if (getExactKeyframeAtTime(currentKeyframes, pendingAddTime)) {
        toast.error(
          <div className="notificationText">
            A keyframe already exists at this time
          </div>
        );
        return;
      }

      const newKeyframeId = `${selectedHitArea.id}-kf-${Date.now()}-${uniqueHitAreaSeedRef.current}`;
      uniqueHitAreaSeedRef.current += 1;
      const newKeyframe = {
        id: newKeyframeId,
        time: pendingAddTime,
        ...keyframeRect,
      };
      let hasVeryCloseNeighbor = false;
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== selectedHitArea.id) return item;
          const currentKeyframes = cloneHitAreaKeyframes(item.keyframes || []);
          hasVeryCloseNeighbor = hasNearbyKeyframeSpacing(
            currentKeyframes,
            pendingAddTime
          );
          return {
            ...item,
            ...keyframeRect,
            keyframes: appendHitAreaKeyframe(currentKeyframes, newKeyframe),
          };
        })
      );
      setSelectedKeyframeId(newKeyframeId);
      setPendingKeyframeTime(null);
      setKeyframePopupState((prev) => ({ ...prev, isOpen: false }));
      setKeyframeEditorErrors({ width: "", height: "" });
      if (hasVeryCloseNeighbor) {
        toast.warning(
          <div className="notificationText">
            Keyframes under 0.5s apart may appear instantaneous
          </div>
        );
      }
    } else {
      setHitAreas((previousAreas) =>
        previousAreas.map((item) => {
          if (item.id !== selectedHitArea.id) return item;
          return {
            ...item,
            ...keyframeRect,
            keyframes: patchKeyframeByIdOnly(
              item.keyframes || [],
              editingKeyframeId,
              keyframeRect
            ),
          };
        })
      );
      setKeyframePopupState((prev) => ({ ...prev, isOpen: false }));
      setKeyframeEditorErrors({ width: "", height: "" });
    }
    queueHistoryCommit();
  }, [
    getCurrentSelectedHitAreaRect,
    keyframeDimensionLimits.maxHeight,
    keyframeDimensionLimits.maxWidth,
    keyframeEditorHeight,
    keyframeEditorWidth,
    queueHistoryCommit,
    resolveKeyframeEditorDimensions,
    resolveTimelineRange,
    selectedHitArea,
  ]);

  const deleteSelectedKeyframe = useCallback(() => {
    if (!selectedHitArea || !selectedHitAreaKeyframe) return;
    if (selectedHitArea?.lockBoolean) return;
    const orderedKeyframes = sortKeyframesByTime(
      cloneHitAreaKeyframes(selectedHitArea.keyframes || [])
    );
    if (!orderedKeyframes.length) return;

    const remainingKeyframes = orderedKeyframes.filter(
      (frame) => String(frame.id) !== String(selectedHitAreaKeyframe.id)
    );
    const fallbackFrame =
      getKeyframeAtTime(remainingKeyframes, currentTimelineSecond) ||
      remainingKeyframes[0] ||
      null;

    setHitAreas((previousAreas) =>
      previousAreas.map((item) => {
        if (item.id !== selectedHitArea.id) return item;
        const baseFrame = remainingKeyframes[0];
        return {
          ...item,
          width: roundDimension(baseFrame?.width ?? item.width),
          height: roundDimension(baseFrame?.height ?? item.height),
          keyframes: cloneHitAreaKeyframes(remainingKeyframes),
        };
      })
    );
    setSelectedKeyframeId(fallbackFrame?.id || null);
    queueHistoryCommit();
    setKeyframePopupState((previousState) =>
      previousState.isOpen ? { ...previousState, isOpen: false } : previousState
    );
    toast.success(
      <div className="notificationText d-flex">
        Keyframe deleted successfully
      </div>
    );
  }, [
    currentTimelineSecond,
    queueHistoryCommit,
    selectedHitArea,
    selectedHitAreaKeyframe,
  ]);

  useEffect(() => {
    if (!open) return undefined;

    const handleModalDeleteKey = (event) => {
      const isDeleteKey =
        event.key === "Delete" ||
        event.key === "Backspace" ||
        event.code === "Delete";
      if (!isDeleteKey) return;

      const target = event.target;
      const activeElement = document.activeElement;
      const isEditableTarget =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable ||
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        activeElement?.isContentEditable ||
        target?.closest?.(
          ".fr-view, .fr-element, .fr-box, .fr-wrapper, [contenteditable='true']"
        ) ||
        activeElement?.closest?.(
          ".fr-view, .fr-element, .fr-box, .fr-wrapper, [contenteditable='true']"
        );

      if (isEditableTarget) return;

      const isKeyframePopupOpen = keyframePopupState.isOpen;
      const isAddKeyframePopup =
        isKeyframePopupOpen && pendingKeyframeTimeRef.current !== null;
      const isUpdateKeyframePopup =
        isKeyframePopupOpen &&
        !!selectedHitAreaKeyframe &&
        pendingKeyframeTimeRef.current === null;

      if (isAddKeyframePopup) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        return;
      }

      if (isUpdateKeyframePopup) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        deleteSelectedKeyframe();
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }

      const liveSelectedHitArea =
        hitAreasRef.current.find((item) => item.id === selectedHitAreaIdRef.current) ||
        null;
      if (
        liveSelectedHitArea &&
        !liveSelectedHitArea.lockBoolean &&
        !pendingKeyframeTimeRef.current
      ) {
        deleteHitAreaById(liveSelectedHitArea.id, event);
      }
    };

    window.addEventListener("keydown", handleModalDeleteKey, true);
    return () => {
      window.removeEventListener("keydown", handleModalDeleteKey, true);
    };
  }, [
    deleteHitAreaById,
    deleteSelectedKeyframe,
    keyframePopupState.isOpen,
    open,
    selectedHitAreaKeyframe,
  ]);

  // Final modal actions: persist sanitized hit areas on save or discard on cancel.
  const submitHitAreas = () => {
    if (isTimelinePlaying) {
      stopTimelinePlayback();
    }
    let areasToPersist = hitAreas;

    if (
      isHitAreaLinkEditing &&
      selectedHitArea &&
      !selectedHitArea?.lockBoolean
    ) {
      const normalizedValue = normalizeHitAreaUrl(hitAreaLinkInput);
      if (normalizedValue !== selectedHitAreaTargetLink) {
        areasToPersist = hitAreas.map((item) =>
          item.id === selectedHitArea.id
            ? { ...item, targetLink: normalizedValue }
            : item
        );
      }
      setHitAreaLinkInput(
        normalizedValue ? normalizedValue : getHitAreaLinkInputSeed(normalizedValue)
      );
      setHitAreaLinkError("");
      setIsHitAreaLinkEditing(false);
    }

    onSave(
      areasToPersist.map((item, index) =>
        sanitizeHitArea(
          item,
          index,
          useAuthoringImageSize ? imageWidth : rawImageWidth,
          useAuthoringImageSize ? imageHeight : rawImageHeight,
          safeDuration,
          imageTimelineStart,
          imageTimelineEnd
        )
      ),
      nextDefaultHitAreaIndexRef.current,
      { hitAreaDraggerWidth: draggerWidth }
    );
    clearUndoRedoHistory();
  };

  const handleCancel = () => {
    if (isTimelinePlaying) {
      stopTimelinePlayback();
    }
    clearUndoRedoHistory();
    onCancel();
  };
  const isPlaybackInteractionAllowed = useCallback((target) => {
    if (!target || typeof target.closest !== "function") return false;
    return !!(
      target.closest(".hitAreaTimelinePlayButton") ||
      target.closest(".hitAreaFooterCancel") ||
      target.closest(".hitAreaFooterSave") ||
      target.closest(".hitAreaTimelineRightBody")
    );
  }, []);

  const blockPlaybackPointerInteraction = useCallback(
    (event) => {
      if (!isTimelinePlaying) return;
      if (isPlaybackInteractionAllowed(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    },
    [isPlaybackInteractionAllowed, isTimelinePlaying]
  );
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      className="hitAreaModalDialog"
      maxWidth={false}
      disableEscapeKeyDown
      transitionDuration={{ enter: 150, exit: 100 }}
      PaperProps={{
        className: `hitAreaModalPaper ${isTimelinePlaying ? "isTimelinePlaying" : ""}`,
        onMouseDownCapture: blockPlaybackPointerInteraction,
        onClickCapture: blockPlaybackPointerInteraction,
        onPointerDownCapture: blockPlaybackPointerInteraction,
      }}
      BackdropProps={{ className: "hitAreaModalBackdrop" }}
    >
      <div className="hitAreaModalHeader">ADD HIT AREA</div>

      <div className="hitAreaModalBody">
        <div
          className={`hitAreaModalLeftRail ${isHitAreaLimitReached ? "isDisabled" : ""}`}
        >
          <div className={`hitAreaDrawTool ${isDrawToolActive ? "isActive" : ""}`}>
            <img src={HitAreaCompBlueIcon} alt="" className="hitAreaDrawToolIcon" />
          </div>
        </div>

        <div className="hitAreaModalMain" ref={modalMainRef}>
      <HitAreaCanvasSection
        isHitAreaLimitReached={isHitAreaLimitReached}
        hitAreaLimitTooltip={HIT_AREA_LIMIT_TOOLTIP}
        imageSource={imageSource}
        isPreviewImageError={isPreviewImageError}
        previewAreaRef={previewAreaRef}
        previewStageRef={previewStageRef}
        canvasAreaRef={canvasAreaRef}
        previewImageRef={previewImageRef}
        previewStageDimensions={previewStageDimensions}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        canDrawHitAreas={canDrawHitAreas}
        isPreviewStageReady={isPreviewStageReady}
        isDrawToolActive={isDrawToolActive}
        handleStageMouseDown={handleStageMouseDown}
        hitAreas={hitAreas}
        selectedHitAreaId={selectedHitAreaId}
        getResolvedHitAreaRectAtTime={getResolvedHitAreaRectAtTime}
        currentTimelineSecond={currentTimelineSecond}
        imageToDisplayRect={imageToDisplayRect}
        boundaryWarningIds={boundaryWarningIds}
        isHitAreaActiveAtCurrentTime={isHitAreaActiveAtCurrentTime}
        selectHitAreaFromCanvas={selectHitAreaFromCanvas}
        createDimensionText={createDimensionText}
        deleteHitAreaById={deleteHitAreaById}
        selectedHitArea={selectedHitArea}
        selectedDisplayRect={selectedDisplayRect}
        isSelectedHitAreaActive={isSelectedHitAreaActive}
        isSelectedHitAreaLocked={isSelectedHitAreaLocked}
        isSelectedHitAreaVisible={isSelectedHitAreaVisible}
        selectedImageRectAtCurrentTime={selectedImageRectAtCurrentTime}
        selectedHitAreaKeyframe={selectedHitAreaKeyframe}
        pendingKeyframeTime={pendingKeyframeTime}
        minRectSize={MIN_RECT_SIZE}
        isHitAreaTransformingRef={isHitAreaTransformingRef}
        applyAlignmentSnapToDisplayRect={applyAlignmentSnapToDisplayRect}
        constrainDisplayRectToNonOverlap={constrainDisplayRectToNonOverlap}
        updateHitAreaFromDisplayRect={updateHitAreaFromDisplayRect}
        setSmartFeedback={setSmartFeedback}
        buildSmartFeedbackFromDisplayRect={buildSmartFeedbackFromDisplayRect}
        queueHistoryCommit={queueHistoryCommit}
        activeLinkPanelHitArea={activeLinkPanelHitArea}
        hitAreaLinkPanelStyle={hitAreaLinkPanelStyle}
        isHitAreaLinkEditing={isHitAreaLinkEditing}
        hitAreaLinkError={hitAreaLinkError}
        hitAreaLinkInput={hitAreaLinkInput}
        selectedHitAreaTargetLink={selectedHitAreaTargetLink}
        beginLinkEditForHitArea={beginLinkEditForHitArea}
        setHitAreaLinkInput={setHitAreaLinkInput}
        setHitAreaLinkError={setHitAreaLinkError}
        setIsHitAreaLinkEditing={setIsHitAreaLinkEditing}
        commitSelectedHitAreaLink={commitSelectedHitAreaLink}
        onHitAreaLinkPanelHeightChange={handleHitAreaLinkPanelHeightChange}
        smartFeedback={smartFeedback}
        draftRect={draftRect}
        displayToImageRect={displayToImageRect}
        setIsPreviewImageLoaded={setIsPreviewImageLoaded}
        setIsPreviewImageError={setIsPreviewImageError}
        refreshStageSize={refreshStageSize}
        isTimelinePlaying={isTimelinePlaying}
        isTimelineMarkerScrubbing={isTimelineMarkerScrubbing}
        getHitAreaModalPreviewElementId={getHitAreaModalPreviewElementId}
      />

      {showTimeline ? (
        <HitAreaTimelineSection
          timelinePanelRef={timelinePanelRef}
          timelineText={timelineText}
          playIcon={PlayDisabled}
          pauseIcon={Pauseicon}
          isTimelinePlaying={isTimelinePlaying}
          onToggleTimelinePlayback={toggleTimelinePlayback}
          canAddKeyframeAtPlayhead={canAddKeyframeAtPlayhead}
          showAddKeyframeButton={showAddKeyframeButton}
          addKeyframeIcon={AddKeyframeIcon}
          onAddKeyframe={addKeyframeAtCurrentTimelineSecond}
          timelineRows={timelineRows}
          editingHitAreaId={editingHitAreaId}
          editingHitAreaName={editingHitAreaName}
          maxHitAreaNameLength={MAX_HIT_AREA_NAME_LENGTH}
          onHitAreaRenameInputChange={handleHitAreaRenameInputChange}
          finishHitAreaRename={finishHitAreaRename}
          beginHitAreaRename={beginHitAreaRename}
          handleTimelineRowClick={handleTimelineRowClick}
          toggleHitAreaLockById={toggleHitAreaLockById}
          toggleHitAreaVisibilityById={toggleHitAreaVisibilityById}
          dragImage={SixDotBlueBg}
          imageCompIcon={ImageCompIcon}
          hitAreaCompIcon={HitAreaCompIcon}
          lockIcon={Lock}
          unlockIcon={UnLock}
          hideIcon={hideIcon}
          eyeIcon={EyeIcon}
          timelineLeftColumnRef={timelineLeftColumnRef}
          handleTimelineLeftScroll={handleTimelineLeftScroll}
          timelineRightColumnRef={timelineRightColumnRef}
          handleTimelineRightScroll={handleTimelineRightScroll}
          timelineRightInnerRef={timelineRightInnerRef}
          safeTimelineWidth={safeTimelineWidth}
          timelineLeftInset={HIT_AREA_TIMELINE_LEFT_INSET}
          timelineScale={timelineScale}
          draggerWidth={draggerWidth}
          setDraggerWidth={setDraggerWidth}
          isResizing={isResizing}
          setIsResizing={setIsResizing}
          modalCanvasAppData={modalCanvasAppData}
          timelineMarkerLeft={timelineMarkerLeft}
          timelineMarkerRef={timelineMarkerRef}
          beginTimelineMarkerDrag={beginTimelineMarkerDrag}
          currentTimelineSecond={currentTimelineSecond}
          secondWidth={secondWidth}
          minTimelineDuration={MIN_TIMELINE_DURATION}
          beginTimelineRangeEdit={beginTimelineRangeEdit}
          openKeyframePopupFromTimelineEventMarker={
            openKeyframePopupFromTimelineEventMarker
          }
          powerIcon={Powericon}
          hasNearbyKeyframeSpacing={hasNearbyKeyframeSpacing}
          clamp={clamp}
          roundTimelineTime={roundTimelineTime}
          beginTimelineKeyframeDrag={beginTimelineKeyframeDrag}
          selectTimelineKeyframe={selectTimelineKeyframe}
        />
      ) : null}
      {showTimeline &&
        keyframePopupState.isOpen &&
        selectedHitArea &&
        (selectedHitAreaKeyframe || pendingKeyframeTime !== null) && (
          <HitAreaKeyframePopup
            keyframePopupState={keyframePopupState}
            hasKeyframeClipboard={hasKeyframeClipboard}
            copySelectedKeyframeDimensions={copySelectedKeyframeDimensions}
            pasteSelectedKeyframeDimensions={pasteSelectedKeyframeDimensions}
            isNewKeyframe={pendingKeyframeTime !== null}
            deleteSelectedKeyframe={deleteSelectedKeyframe}
            closeKeyframePopup={closeKeyframePopup}
            keyframeEditorErrors={keyframeEditorErrors}
            keyframeEditorWidth={keyframeEditorWidth}
            keyframeEditorHeight={keyframeEditorHeight}
            onKeyframeEditorWidthChange={handleKeyframeEditorWidthChange}
            onKeyframeEditorHeightChange={handleKeyframeEditorHeightChange}
            minRectSize={MIN_RECT_SIZE}
            isSelectedHitAreaKeyframeEditable={isSelectedHitAreaKeyframeEditable}
            isKeyframeEditorInvalid={isKeyframeEditorInvalid}
            updateSelectedKeyframeDimensions={updateSelectedKeyframeDimensions}
          />
        )}
        </div>
      </div>

      <div className="hitAreaModalFooter">
        <button
          type="button"
          className="hitAreaFooterCancel"
          onClick={handleCancel}
        >
          CANCEL
        </button>
        <button type="button" className="hitAreaFooterSave" onClick={submitHitAreas}>
          SAVE
        </button>
      </div>
    </Dialog>
  );
}
